/* MOCK DATA — replace with real /v2 API calls when backend is connected */

/**
 * VCM Seed Data — extracted from demo.html sample records
 *
 * All data arrays here are exact replicas of the demo.html JavaScript
 * variables (clients[], caseRecords[], addressRecords[], resources[], documents[]).
 */

import type {
  VcmClient,
  VcmCase,
  VcmGoal,
  VcmHouseholdMember,
  VcmInteraction,
  FollowUp,
  VcmReferral,
  VcmResource,
  VcmDocument,
  AddressRecord,
  CallLogEntry,
} from '../types/vcm';

// ---------------------------------------------------------------------------
// Clients — from demo.html line 368-374
// ---------------------------------------------------------------------------

export const seedClients: VcmClient[] = [
  {
    id: 'VCM-10482',
    firstName: 'Elena',
    middleName: null,
    lastName: 'Martinez',
    nickname: 'Ellie',
    dateOfBirth: '1984-03-17',
    gender: 'Female',
    phoneHome: '512-555-0134',
    phoneAlternate: null,
    email: 'elena.martinez@example.org',
    preferredLanguage: 'Spanish',
    primaryAddress: {
      address1: '407 Willow Bend Dr',
      address2: null,
      city: 'Georgetown',
      county: 'Williamson',
      state: 'TX',
      zip: '78626',
      country: 'US',
    },
    mailingAddress: {
      address1: '1801 Meadow View Rd',
      address2: null,
      city: 'Georgetown',
      county: 'Williamson',
      state: 'TX',
      zip: '78628',
      country: 'US',
    },
    currentResidence: 'Family or friends',
    coApplicantName: 'Luis Martinez',
    coApplicantPhone: '512-555-0167',
    coApplicantRelationship: 'Spouse / Partner',
    headOfHousehold: true,
    totalHouseholdMembers: 4,
    headOfHousehold65OrOlder: false,
    accessOrFunctionalNeeds: false,
    unsafeUnsanitaryHousing: true,
    persistentSocialServiceClient: false,
    significantEmotionalDistress: false,
    singleHeadWithMinors: false,
    serviceNeedsNotes: null,
    clientConnectStatus: 'Ready for Discharge',
    dateOfInitialIntake: '2026-07-15',
    county: 'Williamson',
    organization: 'Texas Veterans Commission',
    program: 'SSVF – Austin Area',
    programCode: 'SSVF-TX-2026-001',
    openCases: 2,
    status: 'Open',
    tier: 'Tier 3',
    manager: 'Noah Golding',
    supervisor: 'Jordan Reed',
    contactOnly: false, contactLevel: 'Bi-weekly',
    militaryRank: 'Sergeant (E-5)',
    militaryBranch: 'Army',
    createdAt: '2026-07-15T10:00:00Z',
    updatedAt: '2026-07-30T14:00:00Z',
  },
  {
    id: 'VCM-10496',
    firstName: 'James',
    middleName: null,
    lastName: 'Nguyen',
    nickname: null,
    dateOfBirth: '1957-11-05',
    gender: 'Male',
    phoneHome: '512-555-0186',
    phoneAlternate: null,
    email: 'jnguyen@example.org',
    preferredLanguage: 'English',
    primaryAddress: {
      address1: '901 Pecan Grove Rd',
      address2: null,
      city: 'Taylor',
      county: 'Williamson',
      state: 'TX',
      zip: '76574',
      country: 'US',
    },
    mailingAddress: null,
    currentResidence: 'Same',
    coApplicantName: null,
    coApplicantPhone: null,
    coApplicantRelationship: null,
    headOfHousehold: true,
    totalHouseholdMembers: 1,
    headOfHousehold65OrOlder: true,
    accessOrFunctionalNeeds: false,
    unsafeUnsanitaryHousing: false,
    persistentSocialServiceClient: false,
    significantEmotionalDistress: false,
    singleHeadWithMinors: false,
    serviceNeedsNotes: null,
    clientConnectStatus: 'Requesting Follow Up',
    dateOfInitialIntake: '2026-07-17',
    county: 'Williamson',
    organization: 'Texas Veterans Commission',
    program: 'SSVF – Austin Area',
    programCode: 'SSVF-TX-2026-001',
    openCases: 1,
    status: 'Review',
    tier: 'Tier 2',
    manager: 'Noah Golding',
    supervisor: 'Jordan Reed',
    contactOnly: false, contactLevel: 'Monthly',
    militaryRank: 'Sergeant First Class (E-7)',
    militaryBranch: 'Army',
    createdAt: '2026-07-17T09:00:00Z',
    updatedAt: '2026-07-29T11:00:00Z',
  },
  {
    id: 'VCM-10517',
    firstName: 'Aisha',
    middleName: null,
    lastName: 'Thompson',
    nickname: null,
    dateOfBirth: '1991-08-22',
    gender: 'Female',
    phoneHome: '737-555-0119',
    phoneAlternate: null,
    email: 'aisha.t@example.org',
    preferredLanguage: 'English',
    primaryAddress: {
      address1: '61 Garden St',
      address2: null,
      city: 'Austin',
      county: 'Travis',
      state: 'TX',
      zip: '78702',
      country: 'US',
    },
    mailingAddress: null,
    currentResidence: 'Emergency Shelter',
    coApplicantName: null,
    coApplicantPhone: null,
    coApplicantRelationship: null,
    headOfHousehold: false,
    totalHouseholdMembers: 1,
    headOfHousehold65OrOlder: false,
    accessOrFunctionalNeeds: true,
    unsafeUnsanitaryHousing: false,
    persistentSocialServiceClient: false,
    significantEmotionalDistress: true,
    singleHeadWithMinors: false,
    serviceNeedsNotes: null,
    clientConnectStatus: 'Ready for Discharge',
    dateOfInitialIntake: '2026-07-19',
    county: 'Travis',
    organization: 'Travis County Veterans Services',
    program: 'SSVF – Austin Area',
    programCode: 'SSVF-TX-2026-001',
    openCases: 1,
    status: 'Open',
    tier: 'Tier 4',
    manager: 'Marcus Lee',
    supervisor: 'Avery Brooks',
    contactOnly: false, contactLevel: 'Weekly',
    militaryRank: 'Petty Officer 2nd Class (E-5)',
    militaryBranch: 'Navy',
    createdAt: '2026-07-19T08:00:00Z',
    updatedAt: '2026-07-31T15:00:00Z',
  },
  {
    id: 'VCM-10528',
    firstName: 'Robert',
    middleName: null,
    lastName: 'Jackson',
    nickname: null,
    dateOfBirth: '1978-02-09',
    gender: 'Male',
    phoneHome: '979-555-0192',
    phoneAlternate: null,
    email: 'rjackson@example.org',
    preferredLanguage: 'English',
    primaryAddress: {
      address1: '18 County Road 419',
      address2: null,
      city: 'Bastrop',
      county: 'Bastrop',
      state: 'TX',
      zip: '78602',
      country: 'US',
    },
    mailingAddress: null,
    currentResidence: 'Same',
    coApplicantName: null,
    coApplicantPhone: null,
    coApplicantRelationship: null,
    headOfHousehold: true,
    totalHouseholdMembers: 2,
    headOfHousehold65OrOlder: false,
    accessOrFunctionalNeeds: false,
    unsafeUnsanitaryHousing: false,
    persistentSocialServiceClient: false,
    significantEmotionalDistress: false,
    singleHeadWithMinors: false,
    serviceNeedsNotes: null,
    clientConnectStatus: 'Ready for Discharge',
    dateOfInitialIntake: '2026-07-21',
    county: 'Bastrop',
    organization: 'Texas Veterans Commission',
    program: 'SSVF – Austin Area',
    programCode: 'SSVF-TX-2026-001',
    openCases: 1,
    status: 'Pending',
    tier: 'Tier 1',
    manager: 'Noah Golding',
    supervisor: 'Jordan Reed',
    contactOnly: false, contactLevel: 'Monthly',
    militaryRank: 'Corporal (E-4)',
    militaryBranch: 'Marine Corps',
    createdAt: '2026-07-21T10:00:00Z',
    updatedAt: '2026-07-21T10:00:00Z',
  },
  {
    id: 'VCM-09832',
    firstName: 'Maria',
    middleName: null,
    lastName: 'Hernandez',
    nickname: null,
    dateOfBirth: '1968-09-14',
    gender: 'Female',
    phoneHome: '830-555-0164',
    phoneAlternate: null,
    email: 'mhernandez@example.org',
    preferredLanguage: 'Spanish',
    primaryAddress: {
      address1: '200 Oak Meadow Ln',
      address2: null,
      city: 'Seguin',
      county: 'Guadalupe',
      state: 'TX',
      zip: '78155',
      country: 'US',
    },
    mailingAddress: null,
    currentResidence: 'Same',
    coApplicantName: null,
    coApplicantPhone: null,
    coApplicantRelationship: null,
    headOfHousehold: true,
    totalHouseholdMembers: 3,
    headOfHousehold65OrOlder: false,
    accessOrFunctionalNeeds: false,
    unsafeUnsanitaryHousing: false,
    persistentSocialServiceClient: false,
    significantEmotionalDistress: false,
    singleHeadWithMinors: false,
    serviceNeedsNotes: null,
    clientConnectStatus: 'All Needs Met',
    dateOfInitialIntake: '2026-05-10',
    county: 'Guadalupe',
    organization: 'Guadalupe County VSO',
    program: 'Veterans Housing Assistance – South TX',
    programCode: 'VHAP-TX-2026-001',
    openCases: 1,
    status: 'Closed',
    tier: 'Tier 2',
    manager: 'Darius Cole',
    supervisor: 'Taylor Morgan',
    contactOnly: false, contactLevel: 'As needed',
    militaryRank: 'Senior Airman (E-4)',
    militaryBranch: 'Air Force',
    createdAt: '2026-05-10T09:00:00Z',
    updatedAt: '2026-06-15T16:00:00Z',
  },
  {
    id: 'VCM-10541',
    firstName: 'David',
    middleName: null,
    lastName: 'Chen',
    nickname: null,
    dateOfBirth: '1972-06-30',
    gender: 'Male',
    phoneHome: '512-555-0211',
    phoneAlternate: null,
    email: 'dchen@example.org',
    preferredLanguage: 'English',
    primaryAddress: {
      address1: '145 Brushy Creek Rd',
      address2: null,
      city: 'Round Rock',
      county: 'Williamson',
      state: 'TX',
      zip: '78664',
      country: 'US',
    },
    mailingAddress: null,
    currentResidence: 'Hotel / motel',
    coApplicantName: null,
    coApplicantPhone: null,
    coApplicantRelationship: null,
    headOfHousehold: true,
    totalHouseholdMembers: 3,
    headOfHousehold65OrOlder: false,
    accessOrFunctionalNeeds: false,
    unsafeUnsanitaryHousing: true,
    persistentSocialServiceClient: false,
    significantEmotionalDistress: true,
    singleHeadWithMinors: true,
    serviceNeedsNotes: null,
    clientConnectStatus: 'Requesting Follow Up',
    dateOfInitialIntake: '2026-07-23',
    county: 'Williamson',
    organization: 'Texas Veterans Commission',
    program: 'SSVF – Austin Area',
    programCode: 'SSVF-TX-2026-001',
    openCases: 1,
    status: 'Open',
    tier: 'Tier 4',
    manager: 'Noah Golding',
    supervisor: 'Jordan Reed',
    contactOnly: false, contactLevel: 'Weekly',
    militaryRank: 'Specialist (E-4)',
    militaryBranch: 'Army',
    createdAt: '2026-07-23T09:00:00Z',
    updatedAt: '2026-08-02T14:00:00Z',
  },
  {
    id: 'VCM-10555',
    firstName: 'Patricia',
    middleName: 'Ann',
    lastName: 'Williams',
    nickname: 'Pat',
    dateOfBirth: '1955-01-12',
    gender: 'Female',
    phoneHome: '737-555-0244',
    phoneAlternate: null,
    email: 'pwilliams@example.org',
    preferredLanguage: 'English',
    primaryAddress: {
      address1: '820 Congress Ave',
      address2: 'Unit 4B',
      city: 'Austin',
      county: 'Travis',
      state: 'TX',
      zip: '78701',
      country: 'US',
    },
    mailingAddress: null,
    currentResidence: 'Emergency Shelter',
    coApplicantName: null,
    coApplicantPhone: null,
    coApplicantRelationship: null,
    headOfHousehold: true,
    totalHouseholdMembers: 1,
    headOfHousehold65OrOlder: true,
    accessOrFunctionalNeeds: true,
    unsafeUnsanitaryHousing: false,
    persistentSocialServiceClient: true,
    significantEmotionalDistress: false,
    singleHeadWithMinors: false,
    serviceNeedsNotes: null,
    clientConnectStatus: 'Requesting Follow Up',
    dateOfInitialIntake: '2026-07-26',
    county: 'Travis',
    organization: 'Travis County Veterans Services',
    program: 'SSVF – Austin Area',
    programCode: 'SSVF-TX-2026-001',
    openCases: 1,
    status: 'Review',
    tier: 'Tier 3',
    manager: 'Marcus Lee',
    supervisor: 'Avery Brooks',
    contactOnly: false, contactLevel: 'Bi-weekly',
    militaryRank: 'Master Sergeant (E-8)',
    militaryBranch: 'Army',
    createdAt: '2026-07-26T10:00:00Z',
    updatedAt: '2026-08-03T11:00:00Z',
  },
  {
    id: 'VCM-10562',
    firstName: 'Samuel',
    middleName: null,
    lastName: 'Rivera',
    nickname: null,
    dateOfBirth: '1989-11-08',
    gender: 'Male',
    phoneHome: '979-555-0277',
    phoneAlternate: '979-555-0278',
    email: 'srivera@example.org',
    preferredLanguage: 'Spanish',
    primaryAddress: {
      address1: '302 Pine St',
      address2: null,
      city: 'Bastrop',
      county: 'Bastrop',
      state: 'TX',
      zip: '78602',
      country: 'US',
    },
    mailingAddress: {
      address1: '1500 Chestnut St',
      address2: null,
      city: 'Bastrop',
      county: 'Bastrop',
      state: 'TX',
      zip: '78602',
      country: 'US',
    },
    currentResidence: 'Family or friends',
    coApplicantName: 'Maria Rivera',
    coApplicantPhone: '979-555-0279',
    coApplicantRelationship: 'Spouse / Partner',
    headOfHousehold: true,
    totalHouseholdMembers: 5,
    headOfHousehold65OrOlder: false,
    accessOrFunctionalNeeds: false,
    unsafeUnsanitaryHousing: true,
    persistentSocialServiceClient: false,
    significantEmotionalDistress: false,
    singleHeadWithMinors: false,
    serviceNeedsNotes: null,
    clientConnectStatus: 'Ready for Discharge',
    dateOfInitialIntake: '2026-07-20',
    county: 'Bastrop',
    organization: 'Texas Veterans Commission',
    program: 'SSVF – Austin Area',
    programCode: 'SSVF-TX-2026-001',
    openCases: 1,
    status: 'Pending',
    tier: 'Tier 2',
    manager: 'Noah Golding',
    supervisor: 'Jordan Reed',
    contactOnly: false, contactLevel: 'Monthly',
    militaryRank: 'Lance Corporal (E-3)',
    militaryBranch: 'Marine Corps',
    createdAt: '2026-07-20T08:00:00Z',
    updatedAt: '2026-07-29T16:00:00Z',
  },
  // Contact-only callers (not yet registered as full clients)
  {
    id: 'CALL-001', firstName: 'Rosa', middleName: null, lastName: 'Delgado', nickname: null,
    dateOfBirth: null, gender: null, phoneHome: '512-555-0777', phoneAlternate: null,
    email: 'rosa.delgado@email.com', preferredLanguage: 'Spanish',
    primaryAddress: { address1: null, address2: null, city: 'Austin', county: 'Travis', state: 'TX', zip: null, country: 'US' },
    mailingAddress: null, currentResidence: null,
    coApplicantName: null, coApplicantPhone: null, coApplicantRelationship: null,
    headOfHousehold: false, totalHouseholdMembers: null,
    headOfHousehold65OrOlder: false, accessOrFunctionalNeeds: false, unsafeUnsanitaryHousing: false,
    persistentSocialServiceClient: false, significantEmotionalDistress: false, singleHeadWithMinors: false,
    serviceNeedsNotes: null, clientConnectStatus: null, dateOfInitialIntake: null,
    county: 'Travis', organization: '', program: 'SSVF – Austin Area', programCode: 'SSVF-TX-2026-001',
    openCases: 0, status: 'Pending', tier: 'Tier 1', manager: null, supervisor: null,
    contactOnly: true, contactLevel: null,
    militaryRank: null,
    militaryBranch: null,
    createdAt: '2026-08-01T10:00:00Z', updatedAt: '2026-08-08T14:00:00Z',
  },
  {
    id: 'CALL-002', firstName: 'Frank', middleName: null, lastName: 'Morrison', nickname: null,
    dateOfBirth: '1962-11-03', gender: 'Male', phoneHome: '737-555-0333', phoneAlternate: null,
    email: null, preferredLanguage: 'English',
    primaryAddress: { address1: '88 River Rd', address2: null, city: 'Bastrop', county: 'Bastrop', state: 'TX', zip: '78602', country: 'US' },
    mailingAddress: null, currentResidence: null,
    coApplicantName: null, coApplicantPhone: null, coApplicantRelationship: null,
    headOfHousehold: true, totalHouseholdMembers: 1,
    headOfHousehold65OrOlder: true, accessOrFunctionalNeeds: false, unsafeUnsanitaryHousing: false,
    persistentSocialServiceClient: false, significantEmotionalDistress: false, singleHeadWithMinors: false,
    serviceNeedsNotes: null, clientConnectStatus: null, dateOfInitialIntake: null,
    county: 'Bastrop', organization: '', program: 'SSVF – Austin Area', programCode: 'SSVF-TX-2026-001',
    openCases: 0, status: 'Pending', tier: 'Tier 1', manager: null, supervisor: null,
    contactOnly: true, contactLevel: null,
    militaryRank: null,
    militaryBranch: null,
    createdAt: '2026-08-03T11:00:00Z', updatedAt: '2026-08-03T11:00:00Z',
  },
];

// ---------------------------------------------------------------------------
// Cases — from demo.html line 376-383
// ---------------------------------------------------------------------------

export const seedCases: VcmCase[] = [
  {
    caseId: 'CASE-4879-10482',
    clientId: 'VCM-10482',
    clientName: 'Elena Martinez',
    program: 'SSVF – Austin Area',
    programCode: 'SSVF-TX-2026-001',
    county: 'Williamson',
    organization: 'Texas Veterans Commission',
    status: 'Open',
    progression: 'Planning',
    tier: 'Tier 3',
    assignedManager: 'Noah Golding',
    managerOrganization: 'Texas Veterans Commission',
    supervisor: 'Jordan Reed',
    contactLevel: 'Bi-weekly',
    dateOfDcmIntake: '2026-07-15',
    referralSource: 'VA Medical Center',
    otherReferralSource: null,
    dateOfArrival: '2026-07-14',
    contactInitiatedBy: 'Client',
    intakeOrganization: 'Texas Veterans Commission',
    intakeLocation: 'Georgetown VSO Office',
    clientRecoveryStatus: 'Ready for Discharge',
    vaFileNumber: '4879-155-0921',
    releaseOfInfoUploaded: true,
    femaReleaseUploaded: true,
    rightsResponsibilitiesUploaded: true,
    verbalConsentRecorded: true,
    authorizationToShare: true,
    ninetyDayNotice: false,
    organizationOptOut: false,
    revokingConsent: false,
    parcelId: 'WM-2024-00407',
    istatRef: {
      incidentId: '26-0017 11JUN Severe Weather',
      assessmentDate: '2026-08-09',
      fieldTeamMember: 'VSO Field Team 3',
      damageLevel: 'Major',
      parcelId: 'WM-2024-00407',
      linkedAt: '2026-08-10',
      oneDriveFolderUrl:
        'https://tdem-my.sharepoint.com/personal/istat/Documents/iSTAT/Williamson/istat-4041872',
      oneDriveFolderName: 'Williamson/istat-4041872',
    },
    istatDiscrepancies: [],
    priorLivingSituation: 'Owned / co-owned residence',
    housingType: 'Single-family home',
    addressVerificationObtained: 'Yes',
    housingCondition: 'Needs Repair',
    numberOfFloors: '1',
    squareFootage: '1,480',
    basement: 'No',
    ownerRenter: 'Owner',
    totalEstDamages: '87,500',
    estInsCoverage: '45,000',
    estUninsuredDamages: '42,500',
    insDeductible: '2,500',
    damageReported: 'Yes',
    reportedAgency: 'VA Medical Center',
    damageNotes: 'Housing in need of accessibility modifications and general repairs.',
    insuranceAdequacy: 'Under Insured',
    insuranceDeductibleRange: '$2,000\u2013$4,999',
    insuranceEntries: [
      {
        id: 'ins-1',
        coverageType: 'VA Healthcare',
        company: 'VA Austin Medical Center',
        claimStatus: 'Approved',
        notes: 'Healthcare enrollment confirmed May 20.',
      },
    ],
    currentResidence: 'Family or friends',
    shelterStatus: 'Not needed',
    shelterName: null,
    currentResidenceNotes: null,
    temporaryHousingStatus: 'Staying with friends or family',
    temporaryHousingStartDate: '2026-07-15',
    temporaryHousingEndDate: '2026-10-31',
    permanentResidenceStatus: 'Repair in progress',
    housingNotes: 'Family can host household through July 2026.',
    vaClaimStatus: 'In Review',
    vaClaimDate: '2026-07-21',
    vaClaimAmount: '$4,200',
    vaAppealStatus: 'Planned',
    femaInitialAppealDate: null,
    femaAppealApprovedAmount: null,
    vaClaimDenialReason: 'Awaiting additional service records',
    educationBenefitStatus: 'Applied',
    educationBenefitAmount: null,
    sbaResponseDate: null,
    appliedForOtherLoans: 'No',
    grossMonthlyIncome: '$4,980',
    monthlyExpenses: '$4,420',
    caseSentToPartner: 'No',
    dateSentToPartner: null,
    partnerCaseStatus: 'Not referred',
    partnerAwardValue: null,
    recoveryPlanDate: '2026-07-18',
    recoveryTimeline: '4 months (120 days)',
    dateClosed: null,
    closureReason: null,
    completedSatisfactionSurvey: null,
    clientSecuredHousing: null,
    householdMemberWorkingPlan: null,
    dateOfProgramClosure: null,
    qualitativeClosureSummary: null,
    closureChecklist: {
      'All service goals reviewed': false,
      'Referral outcomes recorded': false,
      'Final client follow-up completed': false,
      'Required closure documents uploaded': false,
      'Client satisfaction survey offered': false,
      'Case supervisor review completed': false,
    },
    dateDcmpReleaseSigned: null,
    casePriorClosures: null,
    militaryBranch: 'Army',
    eraOfService: 'Post-9/11 (OEF/OIF/OND)',
    dischargeStatus: 'Honorable',
    createdAt: '2026-07-15T10:00:00Z',
    updatedAt: '2026-07-30T14:00:00Z',
  },
  {
    caseId: 'CASE-4879-10482-B',
    clientId: 'VCM-10482',
    clientName: 'Elena Martinez',
    program: 'Veterans Housing Assistance – South TX',
    programCode: 'VHAP-TX-2026-001',
    county: 'Williamson',
    organization: 'Texas Veterans Commission',
    status: 'Review',
    progression: 'Assessing',
    tier: 'Tier 1',
    assignedManager: 'Noah Golding',
    managerOrganization: 'Texas Veterans Commission',
    supervisor: 'Jordan Reed',
    contactLevel: 'Monthly',
    dateOfDcmIntake: '2025-02-10',
    referralSource: null,
    otherReferralSource: null,
    dateOfArrival: null,
    contactInitiatedBy: null,
    intakeOrganization: 'Texas Veterans Commission',
    intakeLocation: null,
    clientRecoveryStatus: 'Requesting Follow Up',
    vaFileNumber: null,
    releaseOfInfoUploaded: false,
    femaReleaseUploaded: false,
    rightsResponsibilitiesUploaded: false,
    verbalConsentRecorded: false,
    authorizationToShare: false,
    ninetyDayNotice: false,
    organizationOptOut: false,
    revokingConsent: false,
    parcelId: 'WM-2024-00407',
    istatRef: null,
    istatDiscrepancies: [],
    priorLivingSituation: null,
    housingType: null,
    addressVerificationObtained: null,
    housingCondition: null,
    numberOfFloors: null,
    squareFootage: null,
    basement: null,
    ownerRenter: null,
    totalEstDamages: null,
    estInsCoverage: null,
    estUninsuredDamages: null,
    insDeductible: null,
    damageReported: null,
    reportedAgency: null,
    damageNotes: null,
    insuranceAdequacy: null,
    insuranceDeductibleRange: null,
    insuranceEntries: [],
    currentResidence: null,
    shelterStatus: null,
    shelterName: null,
    currentResidenceNotes: null,
    temporaryHousingStatus: null,
    temporaryHousingStartDate: null,
    temporaryHousingEndDate: null,
    permanentResidenceStatus: null,
    housingNotes: null,
    vaClaimStatus: null,
    vaClaimDate: null,
    vaClaimAmount: null,
    vaAppealStatus: null,
    femaInitialAppealDate: null,
    femaAppealApprovedAmount: null,
    vaClaimDenialReason: null,
    educationBenefitStatus: null,
    educationBenefitAmount: null,
    sbaResponseDate: null,
    appliedForOtherLoans: null,
    grossMonthlyIncome: null,
    monthlyExpenses: null,
    caseSentToPartner: null,
    dateSentToPartner: null,
    partnerCaseStatus: null,
    partnerAwardValue: null,
    recoveryPlanDate: null,
    recoveryTimeline: null,
    dateClosed: null,
    closureReason: null,
    completedSatisfactionSurvey: null,
    clientSecuredHousing: null,
    householdMemberWorkingPlan: null,
    dateOfProgramClosure: null,
    qualitativeClosureSummary: null,
    closureChecklist: {
      'All service goals reviewed': false,
      'Referral outcomes recorded': false,
      'Final client follow-up completed': false,
      'Required closure documents uploaded': false,
      'Client satisfaction survey offered': false,
      'Case supervisor review completed': false,
    },
    dateDcmpReleaseSigned: null,
    casePriorClosures: null,
    militaryBranch: 'Army',
    eraOfService: 'Post-9/11 (OEF/OIF/OND)',
    dischargeStatus: 'Honorable',
    createdAt: '2026-04-10T09:00:00Z',
    updatedAt: '2026-05-15T11:00:00Z',
  },
  {
    caseId: 'CASE-4879-10496',
    clientId: 'VCM-10496',
    clientName: 'James Nguyen',
    program: 'SSVF – Austin Area',
    programCode: 'SSVF-TX-2026-001',
    county: 'Williamson',
    organization: 'Texas Veterans Commission',
    status: 'Review',
    progression: 'Monitoring',
    tier: 'Tier 2',
    assignedManager: 'Noah Golding',
    managerOrganization: 'Texas Veterans Commission',
    supervisor: 'Jordan Reed',
    contactLevel: 'Monthly',
    dateOfDcmIntake: '2026-07-17',
    referralSource: null,
    otherReferralSource: null,
    dateOfArrival: null,
    contactInitiatedBy: null,
    intakeOrganization: 'Texas Veterans Commission',
    intakeLocation: null,
    clientRecoveryStatus: 'Requesting Follow Up',
    vaFileNumber: null,
    releaseOfInfoUploaded: false,
    femaReleaseUploaded: false,
    rightsResponsibilitiesUploaded: false,
    verbalConsentRecorded: false,
    authorizationToShare: false,
    ninetyDayNotice: false,
    organizationOptOut: false,
    revokingConsent: false,
    parcelId: 'WM-2024-09012',
    istatRef: null,
    istatDiscrepancies: [],
    priorLivingSituation: 'Owned / co-owned residence',
    housingType: 'Single-family home',
    addressVerificationObtained: 'Yes',
    housingCondition: 'Needs Repair',
    numberOfFloors: '1',
    squareFootage: '2,100',
    basement: 'No',
    ownerRenter: 'Owner',
    totalEstDamages: '12,000',
    estInsCoverage: '10,000',
    estUninsuredDamages: '2,000',
    insDeductible: '1,500',
    damageReported: 'Yes',
    reportedAgency: 'VSO Office',
    damageNotes: null,
    insuranceAdequacy: null,
    insuranceDeductibleRange: null,
    insuranceEntries: [],
    currentResidence: null,
    shelterStatus: null,
    shelterName: null,
    currentResidenceNotes: null,
    temporaryHousingStatus: null,
    temporaryHousingStartDate: null,
    temporaryHousingEndDate: null,
    permanentResidenceStatus: null,
    housingNotes: null,
    vaClaimStatus: null,
    vaClaimDate: null,
    vaClaimAmount: null,
    vaAppealStatus: null,
    femaInitialAppealDate: null,
    femaAppealApprovedAmount: null,
    vaClaimDenialReason: null,
    educationBenefitStatus: null,
    educationBenefitAmount: null,
    sbaResponseDate: null,
    appliedForOtherLoans: null,
    grossMonthlyIncome: null,
    monthlyExpenses: null,
    caseSentToPartner: null,
    dateSentToPartner: null,
    partnerCaseStatus: null,
    partnerAwardValue: null,
    recoveryPlanDate: null,
    recoveryTimeline: null,
    dateClosed: null,
    closureReason: null,
    completedSatisfactionSurvey: null,
    clientSecuredHousing: null,
    householdMemberWorkingPlan: null,
    dateOfProgramClosure: null,
    qualitativeClosureSummary: null,
    closureChecklist: {
      'All service goals reviewed': false,
      'Referral outcomes recorded': false,
      'Final client follow-up completed': false,
      'Required closure documents uploaded': false,
      'Client satisfaction survey offered': false,
      'Case supervisor review completed': false,
    },
    dateDcmpReleaseSigned: null,
    casePriorClosures: null,
    militaryBranch: 'Navy',
    eraOfService: 'Vietnam Era',
    dischargeStatus: 'Honorable',
    createdAt: '2026-07-17T09:00:00Z',
    updatedAt: '2026-07-29T11:00:00Z',
  },
  {
    caseId: 'CASE-4879-10517',
    clientId: 'VCM-10517',
    clientName: 'Aisha Thompson',
    program: 'SSVF – Austin Area',
    programCode: 'SSVF-TX-2026-001',
    county: 'Travis',
    organization: 'Travis County Veterans Services',
    status: 'Open',
    progression: 'Advocacy',
    tier: 'Tier 4',
    assignedManager: 'Marcus Lee',
    managerOrganization: 'Travis County Veterans Services',
    supervisor: 'Avery Brooks',
    contactLevel: 'Weekly',
    dateOfDcmIntake: '2026-07-19',
    referralSource: null,
    otherReferralSource: null,
    dateOfArrival: null,
    contactInitiatedBy: null,
    intakeOrganization: 'Travis County Veterans Services',
    intakeLocation: null,
    clientRecoveryStatus: 'Ready for Discharge',
    vaFileNumber: null,
    releaseOfInfoUploaded: false,
    femaReleaseUploaded: false,
    rightsResponsibilitiesUploaded: false,
    verbalConsentRecorded: false,
    authorizationToShare: false,
    ninetyDayNotice: false,
    organizationOptOut: false,
    revokingConsent: false,
    parcelId: 'TR-2024-06100',
    istatRef: null,
    istatDiscrepancies: [],
    priorLivingSituation: 'Applicant rented',
    housingType: 'Apartment',
    addressVerificationObtained: 'Yes',
    housingCondition: 'Homeless',
    numberOfFloors: '3',
    squareFootage: '850',
    basement: 'No',
    ownerRenter: 'Renter',
    totalEstDamages: '35,000',
    estInsCoverage: '8,000',
    estUninsuredDamages: '27,000',
    insDeductible: '500',
    damageReported: 'Yes',
    reportedAgency: 'Community Partner',
    damageNotes: null,
    insuranceAdequacy: null,
    insuranceDeductibleRange: null,
    insuranceEntries: [],
    currentResidence: null,
    shelterStatus: null,
    shelterName: null,
    currentResidenceNotes: null,
    temporaryHousingStatus: null,
    temporaryHousingStartDate: null,
    temporaryHousingEndDate: null,
    permanentResidenceStatus: null,
    housingNotes: null,
    vaClaimStatus: null,
    vaClaimDate: null,
    vaClaimAmount: null,
    vaAppealStatus: null,
    femaInitialAppealDate: null,
    femaAppealApprovedAmount: null,
    vaClaimDenialReason: null,
    educationBenefitStatus: null,
    educationBenefitAmount: null,
    sbaResponseDate: null,
    appliedForOtherLoans: null,
    grossMonthlyIncome: null,
    monthlyExpenses: null,
    caseSentToPartner: null,
    dateSentToPartner: null,
    partnerCaseStatus: null,
    partnerAwardValue: null,
    recoveryPlanDate: null,
    recoveryTimeline: null,
    dateClosed: null,
    closureReason: null,
    completedSatisfactionSurvey: null,
    clientSecuredHousing: null,
    householdMemberWorkingPlan: null,
    dateOfProgramClosure: null,
    qualitativeClosureSummary: null,
    closureChecklist: {
      'All service goals reviewed': false,
      'Referral outcomes recorded': false,
      'Final client follow-up completed': false,
      'Required closure documents uploaded': false,
      'Client satisfaction survey offered': false,
      'Case supervisor review completed': false,
    },
    dateDcmpReleaseSigned: null,
    casePriorClosures: null,
    militaryBranch: 'Army',
    eraOfService: 'Post-9/11 (OEF/OIF/OND)',
    dischargeStatus: 'Honorable',
    createdAt: '2026-07-19T08:00:00Z',
    updatedAt: '2026-07-31T15:00:00Z',
  },
  {
    caseId: 'CASE-4879-10528',
    clientId: 'VCM-10528',
    clientName: 'Robert Jackson',
    program: 'SSVF – Austin Area',
    programCode: 'SSVF-TX-2026-001',
    county: 'Bastrop',
    organization: 'Texas Veterans Commission',
    status: 'Pending',
    progression: 'Intake',
    tier: 'Tier 1',
    assignedManager: 'Noah Golding',
    managerOrganization: 'Texas Veterans Commission',
    supervisor: 'Jordan Reed',
    contactLevel: 'Monthly',
    dateOfDcmIntake: '2026-07-21',
    referralSource: null,
    otherReferralSource: null,
    dateOfArrival: null,
    contactInitiatedBy: null,
    intakeOrganization: 'Texas Veterans Commission',
    intakeLocation: null,
    clientRecoveryStatus: 'Ready for Discharge',
    vaFileNumber: null,
    releaseOfInfoUploaded: false,
    femaReleaseUploaded: false,
    rightsResponsibilitiesUploaded: false,
    verbalConsentRecorded: false,
    authorizationToShare: false,
    ninetyDayNotice: false,
    organizationOptOut: false,
    revokingConsent: false,
    parcelId: 'BA-2024-00418',
    istatRef: null,
    istatDiscrepancies: [],
    priorLivingSituation: 'Owned / co-owned residence',
    housingType: 'Mobile home',
    addressVerificationObtained: 'Yes',
    housingCondition: 'Stable',
    numberOfFloors: '1',
    squareFootage: '960',
    basement: 'No',
    ownerRenter: 'Owner',
    totalEstDamages: '8,500',
    estInsCoverage: '0',
    estUninsuredDamages: '8,500',
    insDeductible: null,
    damageReported: 'Yes',
    reportedAgency: 'VSO Office',
    damageNotes: null,
    insuranceAdequacy: null,
    insuranceDeductibleRange: null,
    insuranceEntries: [],
    currentResidence: null,
    shelterStatus: null,
    shelterName: null,
    currentResidenceNotes: null,
    temporaryHousingStatus: null,
    temporaryHousingStartDate: null,
    temporaryHousingEndDate: null,
    permanentResidenceStatus: null,
    housingNotes: null,
    vaClaimStatus: null,
    vaClaimDate: null,
    vaClaimAmount: null,
    vaAppealStatus: null,
    femaInitialAppealDate: null,
    femaAppealApprovedAmount: null,
    vaClaimDenialReason: null,
    educationBenefitStatus: null,
    educationBenefitAmount: null,
    sbaResponseDate: null,
    appliedForOtherLoans: null,
    grossMonthlyIncome: null,
    monthlyExpenses: null,
    caseSentToPartner: null,
    dateSentToPartner: null,
    partnerCaseStatus: null,
    partnerAwardValue: null,
    recoveryPlanDate: null,
    recoveryTimeline: null,
    dateClosed: null,
    closureReason: null,
    completedSatisfactionSurvey: null,
    clientSecuredHousing: null,
    householdMemberWorkingPlan: null,
    dateOfProgramClosure: null,
    qualitativeClosureSummary: null,
    closureChecklist: {
      'All service goals reviewed': false,
      'Referral outcomes recorded': false,
      'Final client follow-up completed': false,
      'Required closure documents uploaded': false,
      'Client satisfaction survey offered': false,
      'Case supervisor review completed': false,
    },
    dateDcmpReleaseSigned: null,
    casePriorClosures: null,
    militaryBranch: 'Marine Corps',
    eraOfService: 'Gulf War (1990-1991)',
    dischargeStatus: 'Honorable',
    createdAt: '2026-07-21T10:00:00Z',
    updatedAt: '2026-07-21T10:00:00Z',
  },
  {
    caseId: 'CASE-4781-09832',
    clientId: 'VCM-09832',
    clientName: 'Maria Hernandez',
    program: 'Veterans Housing Assistance – South TX',
    programCode: 'VHAP-TX-2026-001',
    county: 'Guadalupe',
    organization: 'Guadalupe County VSO',
    status: 'Closed',
    progression: 'Finishing',
    tier: 'Tier 2',
    assignedManager: 'Darius Cole',
    managerOrganization: 'Guadalupe County VSO',
    supervisor: 'Taylor Morgan',
    contactLevel: 'As needed',
    dateOfDcmIntake: '2026-05-10',
    referralSource: null,
    otherReferralSource: null,
    dateOfArrival: null,
    contactInitiatedBy: null,
    intakeOrganization: 'Guadalupe County VSO',
    intakeLocation: null,
    clientRecoveryStatus: 'All Needs Met',
    vaFileNumber: null,
    releaseOfInfoUploaded: false,
    femaReleaseUploaded: false,
    rightsResponsibilitiesUploaded: false,
    verbalConsentRecorded: false,
    authorizationToShare: false,
    ninetyDayNotice: false,
    organizationOptOut: false,
    revokingConsent: false,
    parcelId: 'GU-2024-02001',
    istatRef: null,
    istatDiscrepancies: [],
    priorLivingSituation: 'Applicant rented',
    housingType: 'Duplex',
    addressVerificationObtained: 'No',
    housingCondition: 'Needs Repair',
    numberOfFloors: '1',
    squareFootage: '1,100',
    basement: 'No',
    ownerRenter: 'Renter',
    totalEstDamages: '22,000',
    estInsCoverage: '0',
    estUninsuredDamages: '22,000',
    insDeductible: null,
    damageReported: 'No',
    reportedAgency: null,
    damageNotes: null,
    insuranceAdequacy: null,
    insuranceDeductibleRange: null,
    insuranceEntries: [],
    currentResidence: null,
    shelterStatus: null,
    shelterName: null,
    currentResidenceNotes: null,
    temporaryHousingStatus: null,
    temporaryHousingStartDate: null,
    temporaryHousingEndDate: null,
    permanentResidenceStatus: null,
    housingNotes: null,
    vaClaimStatus: null,
    vaClaimDate: null,
    vaClaimAmount: null,
    vaAppealStatus: null,
    femaInitialAppealDate: null,
    femaAppealApprovedAmount: null,
    vaClaimDenialReason: null,
    educationBenefitStatus: null,
    educationBenefitAmount: null,
    sbaResponseDate: null,
    appliedForOtherLoans: null,
    grossMonthlyIncome: null,
    monthlyExpenses: null,
    caseSentToPartner: null,
    dateSentToPartner: null,
    partnerCaseStatus: null,
    partnerAwardValue: null,
    recoveryPlanDate: null,
    recoveryTimeline: null,
    dateClosed: '2026-04-15',
    closureReason: 'All needs met',
    completedSatisfactionSurvey: 'Yes',
    clientSecuredHousing: 'Yes',
    householdMemberWorkingPlan: 'No',
    dateOfProgramClosure: '2026-04-15',
    qualitativeClosureSummary: null,
    closureChecklist: {
      'All service goals reviewed': true,
      'Referral outcomes recorded': true,
      'Final client follow-up completed': true,
      'Required closure documents uploaded': true,
      'Client satisfaction survey offered': true,
      'Case supervisor review completed': true,
    },
    dateDcmpReleaseSigned: null,
    casePriorClosures: null,
    militaryBranch: 'Army',
    eraOfService: 'Vietnam Era',
    dischargeStatus: 'Honorable',
    createdAt: '2026-05-10T09:00:00Z',
    updatedAt: '2026-06-15T16:00:00Z',
  },
  {
    caseId: 'CASE-4879-10541',
    clientId: 'VCM-10541',
    clientName: 'David Chen',
    program: 'SSVF – Austin Area',
    programCode: 'SSVF-TX-2026-001',
    county: 'Williamson',
    organization: 'Texas Veterans Commission',
    status: 'Open',
    progression: 'Advocacy',
    tier: 'Tier 4',
    assignedManager: 'Noah Golding',
    managerOrganization: 'Texas Veterans Commission',
    supervisor: 'Jordan Reed',
    contactLevel: 'Weekly',
    dateOfDcmIntake: '2026-07-23',
    referralSource: 'Community Partner',
    otherReferralSource: null,
    dateOfArrival: '2026-07-22',
    contactInitiatedBy: 'Partner organization',
    intakeOrganization: 'Texas Veterans Commission',
    intakeLocation: 'Round Rock VSO Office',
    clientRecoveryStatus: 'Requesting Follow Up',
    vaFileNumber: '4879-177-0443',
    releaseOfInfoUploaded: true,
    femaReleaseUploaded: true,
    rightsResponsibilitiesUploaded: true,
    verbalConsentRecorded: true,
    authorizationToShare: true,
    ninetyDayNotice: false,
    organizationOptOut: false,
    revokingConsent: false,
    parcelId: 'WM-2024-01452',
    istatRef: null,
    istatDiscrepancies: [],
    priorLivingSituation: 'Applicant rented',
    housingType: 'Apartment',
    addressVerificationObtained: 'Yes',
    housingCondition: 'Homeless',
    numberOfFloors: '2',
    squareFootage: '980',
    basement: 'No',
    ownerRenter: 'Renter',
    totalEstDamages: '125,000',
    estInsCoverage: '15,000',
    estUninsuredDamages: '110,000',
    insDeductible: '1,000',
    damageReported: 'Yes',
    reportedAgency: 'Community Partner',
    damageNotes: 'Veteran lost housing and all personal belongings. Currently in transitional housing.',
    insuranceAdequacy: 'Sufficient',
    insuranceDeductibleRange: '$500\u2013$999',
    insuranceEntries: [
      {
        id: 'ins-2',
        coverageType: 'VA Healthcare',
        company: 'VA Austin Medical Center',
        claimStatus: 'Approved',
        notes: 'Enrolled in VA healthcare May 24.',
      },
    ],
    currentResidence: 'Hotel / motel',
    shelterStatus: 'Exited',
    shelterName: null,
    currentResidenceNotes: null,
    temporaryHousingStatus: 'Hotel',
    temporaryHousingStartDate: '2026-07-23',
    temporaryHousingEndDate: '2026-10-31',
    permanentResidenceStatus: 'Relocation planned',
    housingNotes: 'Transitional housing voucher expires June 30. Needs permanent relocation assistance.',
    vaClaimStatus: 'Filed',
    vaClaimDate: '2026-07-29',
    vaClaimAmount: '$2,800',
    vaAppealStatus: 'Not needed',
    femaInitialAppealDate: null,
    femaAppealApprovedAmount: null,
    vaClaimDenialReason: null,
    educationBenefitStatus: 'Not applied',
    educationBenefitAmount: null,
    sbaResponseDate: null,
    appliedForOtherLoans: 'No',
    grossMonthlyIncome: '$3,200',
    monthlyExpenses: '$2,900',
    caseSentToPartner: 'Yes',
    dateSentToPartner: '2026-08-02',
    partnerCaseStatus: 'Pending',
    partnerAwardValue: null,
    recoveryPlanDate: '2026-07-29',
    recoveryTimeline: '3 months (90 days)',
    dateClosed: null,
    closureReason: null,
    completedSatisfactionSurvey: null,
    clientSecuredHousing: null,
    householdMemberWorkingPlan: null,
    dateOfProgramClosure: null,
    qualitativeClosureSummary: null,
    closureChecklist: {
      'All service goals reviewed': false,
      'Referral outcomes recorded': false,
      'Final client follow-up completed': false,
      'Required closure documents uploaded': false,
      'Client satisfaction survey offered': false,
      'Case supervisor review completed': false,
    },
    dateDcmpReleaseSigned: null,
    casePriorClosures: null,
    militaryBranch: 'Air Force',
    eraOfService: 'Post-9/11 (OEF/OIF/OND)',
    dischargeStatus: 'Honorable',
    createdAt: '2026-07-23T09:00:00Z',
    updatedAt: '2026-08-02T14:00:00Z',
  },
  {
    caseId: 'CASE-4879-10555',
    clientId: 'VCM-10555',
    clientName: 'Patricia Williams',
    program: 'SSVF – Austin Area',
    programCode: 'SSVF-TX-2026-001',
    county: 'Travis',
    organization: 'Travis County Veterans Services',
    status: 'Review',
    progression: 'Monitoring',
    tier: 'Tier 3',
    assignedManager: 'Marcus Lee',
    managerOrganization: 'Travis County Veterans Services',
    supervisor: 'Avery Brooks',
    contactLevel: 'Bi-weekly',
    dateOfDcmIntake: '2026-07-26',
    referralSource: 'VA Medical Center',
    otherReferralSource: null,
    dateOfArrival: '2026-07-25',
    contactInitiatedBy: 'Client',
    intakeOrganization: 'Travis County Veterans Services',
    intakeLocation: 'Austin VSO Office',
    clientRecoveryStatus: 'Requesting Follow Up',
    vaFileNumber: '4879-189-0567',
    releaseOfInfoUploaded: true,
    femaReleaseUploaded: false,
    rightsResponsibilitiesUploaded: true,
    verbalConsentRecorded: true,
    authorizationToShare: true,
    ninetyDayNotice: false,
    organizationOptOut: false,
    revokingConsent: false,
    parcelId: 'TR-2024-08200',
    istatRef: null,
    istatDiscrepancies: [],
    priorLivingSituation: 'Applicant rented',
    housingType: 'Condo',
    addressVerificationObtained: 'Yes',
    housingCondition: 'Unsafe',
    numberOfFloors: '1',
    squareFootage: '750',
    basement: 'No',
    ownerRenter: 'Owner',
    totalEstDamages: '52,000',
    estInsCoverage: '30,000',
    estUninsuredDamages: '22,000',
    insDeductible: '2,000',
    damageReported: 'Yes',
    reportedAgency: 'VA Medical Center',
    damageNotes: 'Veteran has mobility issues. Housing is not ADA-accessible. Mold concerns in unit.',
    insuranceAdequacy: null,
    insuranceDeductibleRange: null,
    insuranceEntries: [],
    currentResidence: 'Emergency Shelter',
    shelterStatus: 'Active',
    shelterName: 'Travis County Veterans Emergency Shelter',
    currentResidenceNotes: 'Placed on 5/20. Single occupant, mobility accommodations required.',
    temporaryHousingStatus: 'Shelter',
    temporaryHousingStartDate: '2026-07-25',
    temporaryHousingEndDate: '2026-10-25',
    permanentResidenceStatus: 'Relocation planned',
    housingNotes: 'Client is 71, has mobility issues. Needs accessible housing.',
    vaClaimStatus: 'In Review',
    vaClaimDate: null,
    vaClaimAmount: null,
    vaAppealStatus: null,
    femaInitialAppealDate: null,
    femaAppealApprovedAmount: null,
    vaClaimDenialReason: null,
    educationBenefitStatus: 'Not applied',
    educationBenefitAmount: null,
    sbaResponseDate: null,
    appliedForOtherLoans: 'No',
    grossMonthlyIncome: '$1,850',
    monthlyExpenses: '$1,600',
    caseSentToPartner: 'No',
    dateSentToPartner: null,
    partnerCaseStatus: 'Not referred',
    partnerAwardValue: null,
    recoveryPlanDate: '2026-07-31',
    recoveryTimeline: '5 months (150 days)',
    dateClosed: null,
    closureReason: null,
    completedSatisfactionSurvey: null,
    clientSecuredHousing: null,
    householdMemberWorkingPlan: null,
    dateOfProgramClosure: null,
    qualitativeClosureSummary: null,
    closureChecklist: {
      'All service goals reviewed': false,
      'Referral outcomes recorded': false,
      'Final client follow-up completed': false,
      'Required closure documents uploaded': false,
      'Client satisfaction survey offered': false,
      'Case supervisor review completed': false,
    },
    dateDcmpReleaseSigned: null,
    casePriorClosures: null,
    militaryBranch: 'Army',
    eraOfService: 'Vietnam Era',
    dischargeStatus: 'Honorable',
    createdAt: '2026-07-26T10:00:00Z',
    updatedAt: '2026-08-03T11:00:00Z',
  },
  {
    caseId: 'CASE-4879-10562',
    clientId: 'VCM-10562',
    clientName: 'Samuel Rivera',
    program: 'SSVF – Austin Area',
    programCode: 'SSVF-TX-2026-001',
    county: 'Bastrop',
    organization: 'Texas Veterans Commission',
    status: 'Pending',
    progression: 'Intake',
    tier: 'Tier 2',
    assignedManager: 'Noah Golding',
    managerOrganization: 'Texas Veterans Commission',
    supervisor: 'Jordan Reed',
    contactLevel: 'Monthly',
    dateOfDcmIntake: '2026-07-20',
    referralSource: 'Community Partner',
    otherReferralSource: null,
    dateOfArrival: '2026-07-19',
    contactInitiatedBy: 'Case manager',
    intakeOrganization: 'Texas Veterans Commission',
    intakeLocation: 'Bastrop County VSO Office',
    clientRecoveryStatus: 'Ready for Discharge',
    vaFileNumber: '4879-201-0612',
    releaseOfInfoUploaded: false,
    femaReleaseUploaded: false,
    rightsResponsibilitiesUploaded: false,
    verbalConsentRecorded: true,
    authorizationToShare: false,
    ninetyDayNotice: false,
    organizationOptOut: false,
    revokingConsent: false,
    parcelId: 'BA-2024-00302',
    istatRef: null,
    istatDiscrepancies: [],
    priorLivingSituation: 'Owned / co-owned residence',
    housingType: 'Single-family home',
    addressVerificationObtained: 'No',
    housingCondition: 'Needs Repair',
    numberOfFloors: '1',
    squareFootage: '1,200',
    basement: 'No',
    ownerRenter: 'Owner',
    totalEstDamages: '4,200',
    estInsCoverage: '3,000',
    estUninsuredDamages: '1,200',
    insDeductible: '1,000',
    damageReported: 'Yes',
    reportedAgency: 'VSO Office',
    damageNotes: 'Minor repairs needed. Veteran navigating VA disability claim appeal.',
    insuranceAdequacy: 'Under Insured',
    insuranceDeductibleRange: '$2,000\u2013$4,999',
    insuranceEntries: [
      {
        id: 'ins-3',
        coverageType: 'VA Healthcare',
        company: 'VA Austin Medical Center',
        claimStatus: 'Approved',
        notes: 'VA healthcare enrollment confirmed.',
      },
      {
        id: 'ins-4',
        coverageType: 'TRICARE',
        company: 'TRICARE West',
        claimStatus: 'Pending',
        notes: 'TRICARE eligibility review in progress.',
      },
    ],
    currentResidence: 'Family or friends',
    shelterStatus: 'Not needed',
    shelterName: null,
    currentResidenceNotes: null,
    temporaryHousingStatus: 'Staying with friends or family',
    temporaryHousingStartDate: '2026-07-20',
    temporaryHousingEndDate: null,
    permanentResidenceStatus: 'Repair in progress',
    housingNotes: 'Minor repairs underway. Family staying with in-laws temporarily.',
    vaClaimStatus: 'Denied',
    vaClaimDate: '2026-07-26',
    vaClaimAmount: null,
    vaAppealStatus: 'Planned',
    femaInitialAppealDate: null,
    femaAppealApprovedAmount: null,
    vaClaimDenialReason: 'Insufficient service connection documentation',
    educationBenefitStatus: 'Applied',
    educationBenefitAmount: null,
    sbaResponseDate: null,
    appliedForOtherLoans: 'No',
    grossMonthlyIncome: '$5,400',
    monthlyExpenses: '$4,100',
    caseSentToPartner: 'No',
    dateSentToPartner: null,
    partnerCaseStatus: 'Not referred',
    partnerAwardValue: null,
    recoveryPlanDate: null,
    recoveryTimeline: null,
    dateClosed: null,
    closureReason: null,
    completedSatisfactionSurvey: null,
    clientSecuredHousing: null,
    householdMemberWorkingPlan: null,
    dateOfProgramClosure: null,
    qualitativeClosureSummary: null,
    closureChecklist: {
      'All service goals reviewed': false,
      'Referral outcomes recorded': false,
      'Final client follow-up completed': false,
      'Required closure documents uploaded': false,
      'Client satisfaction survey offered': false,
      'Case supervisor review completed': false,
    },
    dateDcmpReleaseSigned: null,
    casePriorClosures: null,
    militaryBranch: 'Marine Corps',
    eraOfService: 'Post-9/11 (OEF/OIF/OND)',
    dischargeStatus: 'Honorable',
    createdAt: '2026-07-20T08:00:00Z',
    updatedAt: '2026-07-29T16:00:00Z',
  },
];

// ---------------------------------------------------------------------------
// Household Members — from demo.html household tab (line 747-751)
// ---------------------------------------------------------------------------

export const seedHouseholdMembers: VcmHouseholdMember[] = [
  {
    id: 'hh-1',
    clientId: 'VCM-10482',
    firstName: 'Elena',
    lastName: 'Martinez',
    relationship: 'Self',
    dateOfBirth: '1984-03-17',
    condition: 'Not Affected',
    race: 'White',
    ethnicity: 'Hispanic or Latino',
    shelterServiceNeeds: [],
    createdAt: '2026-07-15T10:00:00Z',
    updatedAt: '2026-07-15T10:00:00Z',
  },
  {
    id: 'hh-2',
    clientId: 'VCM-10482',
    firstName: 'Luis',
    lastName: 'Martinez',
    relationship: 'Spouse / Partner',
    dateOfBirth: '1981-07-02',
    condition: 'Not Affected',
    race: 'White',
    ethnicity: 'Hispanic or Latino',
    shelterServiceNeeds: [],
    createdAt: '2026-07-15T10:00:00Z',
    updatedAt: '2026-07-15T10:00:00Z',
  },
  {
    id: 'hh-3',
    clientId: 'VCM-10482',
    firstName: 'Sofia',
    lastName: 'Martinez',
    relationship: 'Child',
    dateOfBirth: '2013-10-18',
    condition: 'Not Affected',
    race: 'White',
    ethnicity: 'Hispanic or Latino',
    shelterServiceNeeds: ['Child Care'],
    createdAt: '2026-07-15T10:00:00Z',
    updatedAt: '2026-07-15T10:00:00Z',
  },
  {
    id: 'hh-4',
    clientId: 'VCM-10541',
    firstName: 'David',
    lastName: 'Chen',
    relationship: 'Self',
    dateOfBirth: '1972-06-30',
    condition: 'Not Affected',
    race: 'Asian',
    ethnicity: 'Not Hispanic or Latino',
    shelterServiceNeeds: [],
    createdAt: '2026-07-23T09:00:00Z',
    updatedAt: '2026-07-23T09:00:00Z',
  },
  {
    id: 'hh-5',
    clientId: 'VCM-10541',
    firstName: 'Lily',
    lastName: 'Chen',
    relationship: 'Child',
    dateOfBirth: '2015-04-11',
    condition: 'Not Affected',
    race: 'Asian',
    ethnicity: 'Not Hispanic or Latino',
    shelterServiceNeeds: ['Child Care'],
    createdAt: '2026-07-23T09:00:00Z',
    updatedAt: '2026-07-23T09:00:00Z',
  },
  {
    id: 'hh-6',
    clientId: 'VCM-10541',
    firstName: 'Max',
    lastName: 'Chen',
    relationship: 'Child',
    dateOfBirth: '2018-09-03',
    condition: 'Not Affected',
    race: 'Asian',
    ethnicity: 'Not Hispanic or Latino',
    shelterServiceNeeds: ['Child Care'],
    createdAt: '2026-07-23T09:00:00Z',
    updatedAt: '2026-07-23T09:00:00Z',
  },
  {
    id: 'hh-7',
    clientId: 'VCM-10555',
    firstName: 'Patricia',
    lastName: 'Williams',
    relationship: 'Self',
    dateOfBirth: '1955-01-12',
    condition: 'Injured',
    race: 'Black or African American',
    ethnicity: 'Not Hispanic or Latino',
    shelterServiceNeeds: ['Health Services', 'Disability Accommodation'],
    createdAt: '2026-07-26T10:00:00Z',
    updatedAt: '2026-07-26T10:00:00Z',
  },
  {
    id: 'hh-8',
    clientId: 'VCM-10562',
    firstName: 'Samuel',
    lastName: 'Rivera',
    relationship: 'Self',
    dateOfBirth: '1989-11-08',
    condition: 'Not Affected',
    race: 'White',
    ethnicity: 'Hispanic or Latino',
    shelterServiceNeeds: [],
    createdAt: '2026-07-20T08:00:00Z',
    updatedAt: '2026-07-20T08:00:00Z',
  },
  {
    id: 'hh-9',
    clientId: 'VCM-10562',
    firstName: 'Maria',
    lastName: 'Rivera',
    relationship: 'Spouse / Partner',
    dateOfBirth: '1991-03-22',
    condition: 'Not Affected',
    race: 'White',
    ethnicity: 'Hispanic or Latino',
    shelterServiceNeeds: [],
    createdAt: '2026-07-20T08:00:00Z',
    updatedAt: '2026-07-20T08:00:00Z',
  },
  {
    id: 'hh-10',
    clientId: 'VCM-10562',
    firstName: 'Carlos',
    lastName: 'Rivera',
    relationship: 'Child',
    dateOfBirth: '2016-07-14',
    condition: 'Not Affected',
    race: 'White',
    ethnicity: 'Hispanic or Latino',
    shelterServiceNeeds: ['Child Care'],
    createdAt: '2026-07-20T08:00:00Z',
    updatedAt: '2026-07-20T08:00:00Z',
  },
  {
    id: 'hh-11',
    clientId: 'VCM-10562',
    firstName: 'Isabella',
    lastName: 'Rivera',
    relationship: 'Child',
    dateOfBirth: '2019-12-01',
    condition: 'Not Affected',
    race: 'White',
    ethnicity: 'Hispanic or Latino',
    shelterServiceNeeds: ['Child Care'],
    createdAt: '2026-07-20T08:00:00Z',
    updatedAt: '2026-07-20T08:00:00Z',
  },
  {
    id: 'hh-12',
    clientId: 'VCM-10562',
    firstName: 'Rosa',
    lastName: 'Rivera',
    relationship: 'Parent',
    dateOfBirth: '1958-05-20',
    condition: 'Not Affected',
    race: 'White',
    ethnicity: 'Hispanic or Latino',
    shelterServiceNeeds: ['Health Services'],
    createdAt: '2026-07-20T08:00:00Z',
    updatedAt: '2026-07-20T08:00:00Z',
  },
];

// ---------------------------------------------------------------------------
// Goals — from demo.html recovery plan tab (line 768-771)
// ---------------------------------------------------------------------------

export const seedGoals: VcmGoal[] = [
  {
    id: 'goal-1',
    caseId: 'CASE-4879-10482',
    clientId: 'VCM-10482',
    name: 'Complete VA disability rating claim',
    recoveryNeed: 'Benefits Navigation',
    startDate: '2026-07-18',
    deadlineDate: '2026-10-31',
    status: 'In Progress',
    complete: false,
    notes: [
      {
        id: 'gn-1',
        text: 'Client has gathered service records and is coordinating with VSO benefits counselor. Waiting on VA decision timeline.',
        timestamp: '2026-07-30T14:00:00Z',
      },
    ],
    createdAt: '2026-07-18T10:00:00Z',
    updatedAt: '2026-07-30T14:00:00Z',
  },
  {
    id: 'goal-2',
    caseId: 'CASE-4879-10482',
    clientId: 'VCM-10482',
    name: 'Enroll in VA healthcare program',
    recoveryNeed: 'Healthcare Navigation',
    startDate: '2026-07-18',
    deadlineDate: '2026-08-10',
    status: 'Goal Partially Met',
    complete: false,
    notes: [],
    createdAt: '2026-07-18T10:00:00Z',
    updatedAt: '2026-07-28T11:00:00Z',
  },
  {
    id: 'goal-3',
    caseId: 'CASE-4879-10541',
    clientId: 'VCM-10541',
    name: 'Secure permanent housing',
    recoveryNeed: 'Housing – Permanent',
    startDate: '2026-07-29',
    deadlineDate: '2026-10-31',
    status: 'In Progress',
    complete: false,
    notes: [
      {
        id: 'gn-2',
        text: 'Contacted three apartment complexes in Round Rock. Two have availability but require deposit assistance.',
        timestamp: '2026-08-02T10:00:00Z',
      },
    ],
    createdAt: '2026-07-29T10:00:00Z',
    updatedAt: '2026-08-02T10:00:00Z',
  },
  {
    id: 'goal-4',
    caseId: 'CASE-4879-10541',
    clientId: 'VCM-10541',
    name: 'Replace essential household items',
    recoveryNeed: 'Financial Assistance',
    startDate: '2026-07-29',
    deadlineDate: '2026-10-15',
    status: 'In Progress',
    complete: false,
    notes: [],
    createdAt: '2026-07-29T10:00:00Z',
    updatedAt: '2026-07-29T10:00:00Z',
  },
  {
    id: 'goal-5',
    caseId: 'CASE-4879-10541',
    clientId: 'VCM-10541',
    name: 'Connect children with school enrollment and support services',
    recoveryNeed: 'Employment & Vocational Rehab',
    startDate: '2026-08-02',
    deadlineDate: '2026-10-15',
    status: 'In Progress',
    complete: false,
    notes: [
      {
        id: 'gn-3',
        text: 'Round Rock ISD contacted. Enrollment paperwork started.',
        timestamp: '2026-08-03T09:00:00Z',
      },
    ],
    createdAt: '2026-08-02T09:00:00Z',
    updatedAt: '2026-08-03T09:00:00Z',
  },
  // CASE-4879-10482 — Elena Martinez (goals 3 & 4 added)
  {
    id: 'goal-1c',
    caseId: 'CASE-4879-10482',
    clientId: 'VCM-10482',
    name: 'Secure stable housing with SSVF',
    recoveryNeed: 'Housing – Temporary',
    startDate: '2026-07-15',
    deadlineDate: '2026-08-10',
    status: 'Goal Met',
    complete: true,
    notes: [{ id: 'gn-1c', text: 'Rapid rehousing deposit covered through SSVF grant. Family moved in June 1.', timestamp: '2026-08-04T10:00:00Z' }],
    createdAt: '2026-07-15T10:00:00Z',
    updatedAt: '2026-08-04T10:00:00Z',
  },
  {
    id: 'goal-1d',
    caseId: 'CASE-4879-10482',
    clientId: 'VCM-10482',
    name: 'Connect to peer support group',
    recoveryNeed: 'Mental Health Services',
    startDate: '2026-08-02',
    deadlineDate: '2026-11-01',
    status: 'In Progress',
    complete: false,
    notes: [{ id: 'gn-1d', text: 'Referred to Austin Vet Center weekly peer support group. First session attended.', timestamp: '2026-08-11T09:00:00Z' }],
    createdAt: '2026-08-02T09:00:00Z',
    updatedAt: '2026-08-11T09:00:00Z',
  },

  // CASE-4879-10541 — David Chen (goal 4 added)
  {
    id: 'goal-5b',
    caseId: 'CASE-4879-10541',
    clientId: 'VCM-10541',
    name: 'File VA disability claim (back injury)',
    recoveryNeed: 'Compensation & Pension',
    startDate: '2026-07-29',
    deadlineDate: '2026-10-28',
    status: 'In Progress',
    complete: false,
    notes: [{ id: 'gn-5b', text: 'Service treatment records requested from NPRC. Estimated 60-day processing window.', timestamp: '2026-08-06T11:00:00Z' }],
    createdAt: '2026-07-29T10:00:00Z',
    updatedAt: '2026-08-06T11:00:00Z',
  },

  // CASE-4879-10555 — Fatima Hassan
  {
    id: 'goal-6',
    caseId: 'CASE-4879-10555',
    clientId: 'VCM-10555',
    name: 'Find accessible permanent housing',
    recoveryNeed: 'Housing – Permanent',
    startDate: '2026-07-31',
    deadlineDate: '2026-11-30',
    status: 'In Progress',
    complete: false,
    notes: [{ id: 'gn-4', text: 'ADA-compliant units limited in Travis County. Expanded search to Hays County.', timestamp: '2026-08-03T11:00:00Z' }],
    createdAt: '2026-07-31T10:00:00Z',
    updatedAt: '2026-08-03T11:00:00Z',
  },
  {
    id: 'goal-7',
    caseId: 'CASE-4879-10555',
    clientId: 'VCM-10555',
    name: 'Restore prescriptions and VA healthcare',
    recoveryNeed: 'Healthcare Navigation',
    startDate: '2026-07-26',
    deadlineDate: '2026-08-11',
    status: 'Goal Met',
    complete: true,
    notes: [{ id: 'gn-5', text: 'VA pharmacy transfer completed. 90-day supply secured through VA Austin.', timestamp: '2026-07-29T14:00:00Z' }],
    createdAt: '2026-07-26T10:00:00Z',
    updatedAt: '2026-07-29T14:00:00Z',
  },
  {
    id: 'goal-7b',
    caseId: 'CASE-4879-10555',
    clientId: 'VCM-10555',
    name: 'Apply for VA disability rating (MST)',
    recoveryNeed: 'Compensation & Pension',
    startDate: '2026-08-06',
    deadlineDate: '2026-11-06',
    status: 'In Progress',
    complete: false,
    notes: [{ id: 'gn-7b', text: 'Attorney referral made for MST-related claim. Gathered buddy statements.', timestamp: '2026-08-13T10:00:00Z' }],
    createdAt: '2026-08-06T10:00:00Z',
    updatedAt: '2026-08-13T10:00:00Z',
  },
  {
    id: 'goal-7c',
    caseId: 'CASE-4879-10555',
    clientId: 'VCM-10555',
    name: 'Enroll in vocational rehab (Ch. 31)',
    recoveryNeed: 'Employment & Vocational Rehab',
    startDate: '2026-08-10',
    deadlineDate: '2026-11-10',
    status: 'In Progress',
    complete: false,
    notes: [],
    createdAt: '2026-08-10T10:00:00Z',
    updatedAt: '2026-08-10T10:00:00Z',
  },

  // CASE-4879-10496 — James Nguyen (4 goals)
  {
    id: 'goal-8',
    caseId: 'CASE-4879-10496',
    clientId: 'VCM-10496',
    name: 'Appeal denied VA pension claim',
    recoveryNeed: 'Compensation & Pension',
    startDate: '2026-07-17',
    deadlineDate: '2026-10-17',
    status: 'In Progress',
    complete: false,
    notes: [{ id: 'gn-8', text: 'NOD filed with VA. Awaiting docket assignment. Accredited attorney engaged.', timestamp: '2026-07-21T09:00:00Z' }],
    createdAt: '2026-07-17T09:00:00Z',
    updatedAt: '2026-07-21T09:00:00Z',
  },
  {
    id: 'goal-9',
    caseId: 'CASE-4879-10496',
    clientId: 'VCM-10496',
    name: 'Coordinate in-home caregiver services',
    recoveryNeed: 'Caregiver Support',
    startDate: '2026-07-21',
    deadlineDate: '2026-08-04',
    status: 'Goal Met',
    complete: true,
    notes: [{ id: 'gn-9', text: 'VA Caregiver Support Program approved. Caregiver stipend begins June 15.', timestamp: '2026-08-09T11:00:00Z' }],
    createdAt: '2026-07-21T09:00:00Z',
    updatedAt: '2026-08-09T11:00:00Z',
  },
  {
    id: 'goal-10',
    caseId: 'CASE-4879-10496',
    clientId: 'VCM-10496',
    name: 'Enroll in VA home-based primary care',
    recoveryNeed: 'Healthcare Navigation',
    startDate: '2026-08-02',
    deadlineDate: '2026-10-15',
    status: 'In Progress',
    complete: false,
    notes: [],
    createdAt: '2026-08-02T09:00:00Z',
    updatedAt: '2026-08-02T09:00:00Z',
  },
  {
    id: 'goal-11',
    caseId: 'CASE-4879-10496',
    clientId: 'VCM-10496',
    name: 'Establish monthly financial management plan',
    recoveryNeed: 'Financial Assistance',
    startDate: '2026-08-10',
    deadlineDate: '2026-11-10',
    status: 'In Progress',
    complete: false,
    notes: [{ id: 'gn-11', text: 'Budget worksheet completed with caseworker. Direct deposit to fiduciary account set up.', timestamp: '2026-08-15T10:00:00Z' }],
    createdAt: '2026-08-10T10:00:00Z',
    updatedAt: '2026-08-15T10:00:00Z',
  },

  // CASE-4879-10517 — Aisha Thompson (4 goals)
  {
    id: 'goal-12',
    caseId: 'CASE-4879-10517',
    clientId: 'VCM-10517',
    name: 'Transition out of emergency shelter',
    recoveryNeed: 'Homeless Prevention',
    startDate: '2026-07-19',
    deadlineDate: '2026-10-01',
    status: 'In Progress',
    complete: false,
    notes: [{ id: 'gn-12', text: 'HUD-VASH voucher application submitted. Waiting on Housing Authority lottery.', timestamp: '2026-07-26T10:00:00Z' }],
    createdAt: '2026-07-19T08:00:00Z',
    updatedAt: '2026-07-26T10:00:00Z',
  },
  {
    id: 'goal-13',
    caseId: 'CASE-4879-10517',
    clientId: 'VCM-10517',
    name: 'Begin PTSD treatment at VA Vet Center',
    recoveryNeed: 'Mental Health Services',
    startDate: '2026-07-23',
    deadlineDate: '2026-10-23',
    status: 'In Progress',
    complete: false,
    notes: [{ id: 'gn-13', text: 'Intake appointment completed at Austin Vet Center. CPT therapy starts June 3.', timestamp: '2026-07-31T14:00:00Z' }],
    createdAt: '2026-07-23T08:00:00Z',
    updatedAt: '2026-07-31T14:00:00Z',
  },
  {
    id: 'goal-14',
    caseId: 'CASE-4879-10517',
    clientId: 'VCM-10517',
    name: 'Submit VA disability claim (PTSD, 100%)',
    recoveryNeed: 'Compensation & Pension',
    startDate: '2026-08-02',
    deadlineDate: '2026-11-01',
    status: 'In Progress',
    complete: false,
    notes: [],
    createdAt: '2026-08-02T08:00:00Z',
    updatedAt: '2026-08-02T08:00:00Z',
  },
  {
    id: 'goal-15',
    caseId: 'CASE-4879-10517',
    clientId: 'VCM-10517',
    name: 'Enroll in GI Bill for nursing program',
    recoveryNeed: 'Education Benefits (GI Bill)',
    startDate: '2026-08-08',
    deadlineDate: '2026-11-08',
    status: 'In Progress',
    complete: false,
    notes: [{ id: 'gn-15', text: 'COE requested. Austin Community College application submitted for fall semester.', timestamp: '2026-08-12T09:00:00Z' }],
    createdAt: '2026-08-08T08:00:00Z',
    updatedAt: '2026-08-12T09:00:00Z',
  },

  // CASE-4879-10528 — Robert Jackson (4 goals)
  {
    id: 'goal-16',
    caseId: 'CASE-4879-10528',
    clientId: 'VCM-10528',
    name: 'Initiate VA disability rating increase',
    recoveryNeed: 'Compensation & Pension',
    startDate: '2026-07-21',
    deadlineDate: '2026-10-21',
    status: 'In Progress',
    complete: false,
    notes: [{ id: 'gn-16', text: 'C&P exam scheduled for June 18. Buddy letters gathered from unit members.', timestamp: '2026-08-02T10:00:00Z' }],
    createdAt: '2026-07-21T10:00:00Z',
    updatedAt: '2026-08-02T10:00:00Z',
  },
  {
    id: 'goal-17',
    caseId: 'CASE-4879-10528',
    clientId: 'VCM-10528',
    name: 'Enroll dependents in CHAMPVA',
    recoveryNeed: 'Healthcare Navigation',
    startDate: '2026-07-26',
    deadlineDate: '2026-08-07',
    status: 'Goal Met',
    complete: true,
    notes: [{ id: 'gn-17', text: 'CHAMPVA enrollment packet submitted. Cards received June 22.', timestamp: '2026-08-18T14:00:00Z' }],
    createdAt: '2026-07-26T10:00:00Z',
    updatedAt: '2026-08-18T14:00:00Z',
  },
  {
    id: 'goal-18',
    caseId: 'CASE-4879-10528',
    clientId: 'VCM-10528',
    name: 'Connect with peer employment specialist',
    recoveryNeed: 'Employment & Vocational Rehab',
    startDate: '2026-08-07',
    deadlineDate: '2026-11-07',
    status: 'In Progress',
    complete: false,
    notes: [],
    createdAt: '2026-08-07T10:00:00Z',
    updatedAt: '2026-08-07T10:00:00Z',
  },
  {
    id: 'goal-19',
    caseId: 'CASE-4879-10528',
    clientId: 'VCM-10528',
    name: 'Apply for property tax exemption (100%)',
    recoveryNeed: 'Benefits Navigation',
    startDate: '2026-08-08',
    deadlineDate: '2026-11-08',
    status: 'In Progress',
    complete: false,
    notes: [{ id: 'gn-19', text: 'Bastrop County Appraisal District application begun. Awaiting VA rating letter.', timestamp: '2026-08-15T10:00:00Z' }],
    createdAt: '2026-08-08T10:00:00Z',
    updatedAt: '2026-08-15T10:00:00Z',
  },

  // CASE-4781-09832 — Maria Hernandez (4 goals, closed case — all goals complete or closed)
  {
    id: 'goal-20',
    caseId: 'CASE-4781-09832',
    clientId: 'VCM-09832',
    name: 'Obtain DD-214 and verify discharge status',
    recoveryNeed: 'Benefits Navigation',
    startDate: '2026-05-10',
    deadlineDate: '2026-06-10',
    status: 'Goal Met',
    complete: true,
    notes: [{ id: 'gn-20', text: 'DD-214 received from NPRC. Discharge status confirmed as Honorable.', timestamp: '2026-06-05T10:00:00Z' }],
    createdAt: '2026-05-10T09:00:00Z',
    updatedAt: '2026-06-05T10:00:00Z',
  },
  {
    id: 'goal-21',
    caseId: 'CASE-4781-09832',
    clientId: 'VCM-09832',
    name: 'Enroll in VA healthcare (Priority Group 3)',
    recoveryNeed: 'Healthcare Navigation',
    startDate: '2026-05-15',
    deadlineDate: '2026-06-15',
    status: 'Goal Met',
    complete: true,
    notes: [{ id: 'gn-21', text: 'VA enrollment confirmed at Audie Murphy VAMC San Antonio. First appointment scheduled.', timestamp: '2026-06-12T11:00:00Z' }],
    createdAt: '2026-05-15T09:00:00Z',
    updatedAt: '2026-06-12T11:00:00Z',
  },
  {
    id: 'goal-22',
    caseId: 'CASE-4781-09832',
    clientId: 'VCM-09832',
    name: 'Access financial assistance for utilities',
    recoveryNeed: 'Financial Assistance',
    startDate: '2026-05-20',
    deadlineDate: '2026-06-30',
    status: 'Goal Met',
    complete: true,
    notes: [{ id: 'gn-22', text: 'LIHEAP application approved. Utility arrears cleared.', timestamp: '2026-06-28T10:00:00Z' }],
    createdAt: '2026-05-20T09:00:00Z',
    updatedAt: '2026-06-28T10:00:00Z',
  },
  {
    id: 'goal-23',
    caseId: 'CASE-4781-09832',
    clientId: 'VCM-09832',
    name: 'Submit surviving spouse pension claim',
    recoveryNeed: 'Compensation & Pension',
    startDate: '2026-05-25',
    deadlineDate: '2026-07-25',
    status: 'Goal Partially Met',
    complete: false,
    notes: [{ id: 'gn-23', text: 'DIC claim submitted. Awaiting VA decision — expected 4–6 months.', timestamp: '2026-06-30T10:00:00Z' }],
    createdAt: '2026-05-25T09:00:00Z',
    updatedAt: '2026-06-30T10:00:00Z',
  },

  // CASE-4879-10562 (4 goals)
  {
    id: 'goal-24',
    caseId: 'CASE-4879-10562',
    clientId: 'VCM-10562',
    name: 'Prevent eviction — rental assistance',
    recoveryNeed: 'Homeless Prevention',
    startDate: '2026-07-29',
    deadlineDate: '2026-08-04',
    status: 'Goal Met',
    complete: true,
    notes: [{ id: 'gn-24', text: 'SSVF rapid assistance approved. Two months back-rent paid directly to landlord.', timestamp: '2026-08-11T09:00:00Z' }],
    createdAt: '2026-07-29T10:00:00Z',
    updatedAt: '2026-08-11T09:00:00Z',
  },
  {
    id: 'goal-25',
    caseId: 'CASE-4879-10562',
    clientId: 'VCM-10562',
    name: 'Enroll in substance use recovery program',
    recoveryNeed: 'Substance Use Recovery',
    startDate: '2026-08-02',
    deadlineDate: '2026-11-01',
    status: 'In Progress',
    complete: false,
    notes: [{ id: 'gn-25', text: 'VA MATS program intake completed. IOP track begins week of June 9.', timestamp: '2026-08-08T10:00:00Z' }],
    createdAt: '2026-08-02T10:00:00Z',
    updatedAt: '2026-08-08T10:00:00Z',
  },
  {
    id: 'goal-26',
    caseId: 'CASE-4879-10562',
    clientId: 'VCM-10562',
    name: 'File VA disability claim (TBI, hearing loss)',
    recoveryNeed: 'Compensation & Pension',
    startDate: '2026-08-10',
    deadlineDate: '2026-11-10',
    status: 'In Progress',
    complete: false,
    notes: [],
    createdAt: '2026-08-10T10:00:00Z',
    updatedAt: '2026-08-10T10:00:00Z',
  },
  {
    id: 'goal-27',
    caseId: 'CASE-4879-10562',
    clientId: 'VCM-10562',
    name: 'Resume employment with job skills training',
    recoveryNeed: 'Employment & Vocational Rehab',
    startDate: '2026-08-12',
    deadlineDate: '2026-11-12',
    status: 'In Progress',
    complete: false,
    notes: [{ id: 'gn-27', text: 'Referred to TWC Veteran Employment Services. Resume workshop attended.', timestamp: '2026-08-17T09:00:00Z' }],
    createdAt: '2026-08-12T10:00:00Z',
    updatedAt: '2026-08-17T09:00:00Z',
  },
];

// ---------------------------------------------------------------------------
// Interactions (Case Notes) — from demo.html notes tab (line 780-782)
// ---------------------------------------------------------------------------

export const seedInteractions: VcmInteraction[] = [
  {
    id: 'int-1',
    caseId: 'CASE-4879-10482',
    clientId: 'VCM-10482',
    noteType: 'Client contact',
    interactionDate: '2026-07-30',
    narrative:
      'Spoke with client regarding VA disability claim status and documentation requirements. Client will gather additional service records before the next follow-up. Connected to VSO benefits counselor for claim assistance.',
    createdBy: 'Noah Golding',
    createdAt: '2026-07-30T14:00:00Z',
  },
  {
    id: 'int-2',
    caseId: 'CASE-4879-10482',
    clientId: 'VCM-10482',
    noteType: 'Home visit',
    interactionDate: '2026-07-23',
    narrative: 'Home assessment completed. Housing condition reviewed and service plan updated.',
    createdBy: 'Noah Golding',
    createdAt: '2026-07-23T10:00:00Z',
  },
  {
    id: 'int-istat-1',
    caseId: 'CASE-4879-10482',
    clientId: 'VCM-10482',
    noteType: 'Case note',
    interactionDate: '2026-08-11',
    narrative:
      '--- Housing Assessment Linked (Notes Only) ---\n' +
      'Assessment ID: HA-2026-0510 · Field Team: T. Reeves · Date: 2026-08-09\n' +
      'Housing Condition: Unsafe (case record: Needs Repair — NOT updated)\n' +
      'Owner/Renter: Owner · Insurance: Yes (VA Healthcare)\n' +
      'Accessibility Needs: Ramp, wide doorways, accessible bathroom\n' +
      'Comments: Second assessment at this address. Prior repair work incomplete. Inspector recommends immediate accessibility modifications.\n' +
      '--- End Assessment Summary ---',
    createdBy: 'System (Assessment Import)',
    createdAt: '2026-08-11T12:00:00Z',
  },
  {
    id: 'int-3',
    caseId: 'CASE-4879-10541',
    clientId: 'VCM-10541',
    noteType: 'Client contact',
    interactionDate: '2026-08-02',
    narrative:
      'Veteran called to discuss housing options. Transitional housing voucher expires June 30. Urgent need for permanent relocation. Reviewed SSVF rental assistance eligibility and next steps.',
    createdBy: 'Noah Golding',
    createdAt: '2026-08-02T14:00:00Z',
  },
  {
    id: 'int-4',
    caseId: 'CASE-4879-10541',
    clientId: 'VCM-10541',
    noteType: 'Partner coordination',
    interactionDate: '2026-07-29',
    narrative:
      'Coordinated with DAV (Disabled American Veterans) chapter. Case submitted for benefits counseling review. Awaiting determination on household goods assistance.',
    createdBy: 'Noah Golding',
    createdAt: '2026-07-29T11:00:00Z',
  },
  {
    id: 'int-5',
    caseId: 'CASE-4879-10541',
    clientId: 'VCM-10541',
    noteType: 'Home visit',
    interactionDate: '2026-07-25',
    narrative:
      'Visited veteran at transitional housing site. Loss of all personal belongings confirmed. Veteran experiencing significant emotional distress. Referred to VA mental health services.',
    createdBy: 'Noah Golding',
    createdAt: '2026-07-25T09:00:00Z',
  },
  {
    id: 'int-6',
    caseId: 'CASE-4879-10555',
    clientId: 'VCM-10555',
    noteType: 'Client contact',
    interactionDate: '2026-08-03',
    narrative:
      'Spoke with veteran at shelter. Mobility is worsening. Needs ADA-compliant ground floor unit. Referred to physical rehabilitation services and disability accommodation assessment.',
    createdBy: 'Marcus Lee',
    createdAt: '2026-08-03T11:00:00Z',
  },
  {
    id: 'int-7',
    caseId: 'CASE-4879-10555',
    clientId: 'VCM-10555',
    noteType: 'Case note',
    interactionDate: '2026-07-29',
    narrative:
      'Prescription medications restored through VA pharmacy transfer. Veteran stable for now but accessible housing remains primary concern.',
    createdBy: 'Marcus Lee',
    createdAt: '2026-07-29T14:00:00Z',
  },
  {
    id: 'int-8',
    caseId: 'CASE-4879-10562',
    clientId: 'VCM-10562',
    noteType: 'Client contact',
    interactionDate: '2026-07-29',
    narrative:
      'Initial intake call. Family of 5 including elderly mother. Veteran navigating VA disability claim denial. Planning appeal with legal assistance support from Texas Veterans Commission.',
    createdBy: 'Noah Golding',
    createdAt: '2026-07-29T16:00:00Z',
  },
];

// ---------------------------------------------------------------------------
// Follow-ups — from demo.html notes tab (line 781)
// ---------------------------------------------------------------------------

export const seedFollowUps: FollowUp[] = [
  {
    id: 'fu-1',
    caseId: 'CASE-4879-10482',
    phoneNumber: '512-555-0134',
    email: 'elena.martinez@example.org',
    bestTimeToContact: 'Weekdays after 4:00 PM',
    nextFollowUpDate: '2026-09-05',
    followUpType: 'Phone',
    outcome: 'Awaiting follow-up',
    notes: 'Review VA claim documentation and service plan checklist.',
  },
  {
    id: 'fu-2',
    caseId: 'CASE-4879-10541',
    phoneNumber: '512-555-0211',
    email: 'dchen@example.org',
    bestTimeToContact: 'Evenings after 6:00 PM',
    nextFollowUpDate: '2026-09-08',
    followUpType: 'Phone',
    outcome: 'Awaiting follow-up',
    notes: 'Check on apartment search progress and transitional housing extension.',
  },
  {
    id: 'fu-3',
    caseId: 'CASE-4879-10555',
    phoneNumber: '737-555-0244',
    email: 'pwilliams@example.org',
    bestTimeToContact: 'Mornings before noon',
    nextFollowUpDate: '2026-09-09',
    followUpType: 'In-person',
    outcome: 'Awaiting follow-up',
    notes: 'Visit at shelter to assess mobility and accessible housing needs.',
  },
];

// ---------------------------------------------------------------------------
// Referrals — from demo.html referrals tab (line 774-777)
// ---------------------------------------------------------------------------

export const seedReferrals: VcmReferral[] = [
  {
    id: 'ref-1',
    caseId: 'CASE-4879-10482',
    clientId: 'VCM-10482',
    interactionId: 'INT-22081',
    serviceName: 'VA disability benefits counseling',
    agencyName: 'Texas Veterans Commission',
    status: 'Referred',
    units: 1,
    costPerUnit: null,
    amount: null,
    fundingAmount: null,
    notes: 'Benefits counseling appointment scheduled.',
    requestedDate: null,
    referredDate: '2026-07-21',
    acceptedDate: null,
    providedDate: null,
    declinedDate: null,
    unreachedDate: null,
    createdAt: '2026-07-21T10:00:00Z',
    updatedAt: '2026-07-21T10:00:00Z',
  },
  {
    id: 'ref-2',
    caseId: 'CASE-4879-10482',
    clientId: 'VCM-10482',
    interactionId: 'INT-22002',
    serviceName: 'Emergency food assistance',
    agencyName: 'Central Texas Food Bank',
    status: 'Provided',
    units: 2,
    costPerUnit: null,
    amount: 300,
    fundingAmount: null,
    notes: null,
    requestedDate: null,
    referredDate: null,
    acceptedDate: null,
    providedDate: '2026-07-19',
    declinedDate: null,
    unreachedDate: null,
    createdAt: '2026-07-19T10:00:00Z',
    updatedAt: '2026-07-19T10:00:00Z',
  },
  {
    id: 'ref-3',
    caseId: 'CASE-4879-10541',
    clientId: 'VCM-10541',
    interactionId: 'INT-22103',
    serviceName: 'SSVF rental deposit assistance',
    agencyName: 'DAV (Disabled American Veterans)',
    status: 'Requested',
    units: 1,
    costPerUnit: null,
    amount: 2400,
    fundingAmount: null,
    notes: 'Awaiting partner determination before proceeding.',
    requestedDate: '2026-08-02',
    referredDate: null,
    acceptedDate: null,
    providedDate: null,
    declinedDate: null,
    unreachedDate: null,
    createdAt: '2026-08-02T10:00:00Z',
    updatedAt: '2026-08-02T10:00:00Z',
  },
  {
    id: 'ref-4',
    caseId: 'CASE-4879-10541',
    clientId: 'VCM-10541',
    interactionId: 'INT-22104',
    serviceName: 'Emergency food assistance',
    agencyName: 'Central Texas Food Bank',
    status: 'Provided',
    units: 3,
    costPerUnit: null,
    amount: 450,
    fundingAmount: null,
    notes: null,
    requestedDate: null,
    referredDate: null,
    acceptedDate: null,
    providedDate: '2026-07-25',
    declinedDate: null,
    unreachedDate: null,
    createdAt: '2026-07-25T10:00:00Z',
    updatedAt: '2026-07-25T10:00:00Z',
  },
  {
    id: 'ref-5',
    caseId: 'CASE-4879-10541',
    clientId: 'VCM-10541',
    interactionId: 'INT-22105',
    serviceName: 'Household goods and furniture assistance',
    agencyName: 'American Legion Post 76',
    status: 'Referred',
    units: 1,
    costPerUnit: null,
    amount: 1500,
    fundingAmount: null,
    notes: 'Needs beds, table, basic kitchen items.',
    requestedDate: '2026-07-29',
    referredDate: '2026-07-31',
    acceptedDate: null,
    providedDate: null,
    declinedDate: null,
    unreachedDate: null,
    createdAt: '2026-07-29T10:00:00Z',
    updatedAt: '2026-07-31T10:00:00Z',
  },
  {
    id: 'ref-6',
    caseId: 'CASE-4879-10555',
    clientId: 'VCM-10555',
    interactionId: 'INT-22201',
    serviceName: 'Physical rehabilitation and disability assessment',
    agencyName: 'Travis County Veterans Services',
    status: 'Referred',
    units: 1,
    costPerUnit: null,
    amount: null,
    fundingAmount: null,
    notes: 'Mobility evaluation and assistive device assessment requested.',
    requestedDate: '2026-07-29',
    referredDate: '2026-08-03',
    acceptedDate: null,
    providedDate: null,
    declinedDate: null,
    unreachedDate: null,
    createdAt: '2026-07-29T10:00:00Z',
    updatedAt: '2026-08-03T10:00:00Z',
  },
  {
    id: 'ref-7',
    caseId: 'CASE-4879-10555',
    clientId: 'VCM-10555',
    interactionId: 'INT-22202',
    serviceName: 'Mental health counseling services',
    agencyName: 'VA Austin Mental Health Clinic',
    status: 'Requested',
    units: 1,
    costPerUnit: null,
    amount: null,
    fundingAmount: null,
    notes: 'Veteran reports anxiety and depression related to housing instability.',
    requestedDate: '2026-08-02',
    referredDate: null,
    acceptedDate: null,
    providedDate: null,
    declinedDate: null,
    unreachedDate: null,
    createdAt: '2026-08-02T10:00:00Z',
    updatedAt: '2026-08-02T10:00:00Z',
  },
  {
    id: 'ref-8',
    caseId: 'CASE-4879-10562',
    clientId: 'VCM-10562',
    interactionId: 'INT-22301',
    serviceName: 'VA disability claim appeal assistance',
    agencyName: 'Central Texas Legal Aid – Veterans Desk',
    status: 'Referred',
    units: 1,
    costPerUnit: null,
    amount: null,
    fundingAmount: null,
    notes: 'Legal assistance for VA disability claim denial appeal.',
    requestedDate: '2026-07-29',
    referredDate: '2026-07-30',
    acceptedDate: null,
    providedDate: null,
    declinedDate: null,
    unreachedDate: null,
    createdAt: '2026-07-29T10:00:00Z',
    updatedAt: '2026-07-30T10:00:00Z',
  },
];

// ---------------------------------------------------------------------------
// Resources — from demo.html line 394-401
// ---------------------------------------------------------------------------

export const seedResources: VcmResource[] = [
  {
    id: 'res-1',
    name: 'Texas Veterans Commission – Benefits Navigation',
    category: 'Benefits Navigation',
    county: 'Williamson',
    service: 'VA disability claims, appeals, and benefits counseling',
    organization: 'Texas Veterans Commission',
    status: 'Active',
    geographicArea: null,
    contactInfo: null,
    eligibilityNotes: 'Must be Texas veteran or eligible dependent.',
    createdAt: '2026-01-01T00:00:00Z',
    updatedAt: '2026-05-29T10:00:00Z',
  },
  {
    id: 'res-2',
    name: 'Central Texas Legal Aid – Veterans Desk',
    category: 'Legal Assistance',
    county: 'Travis',
    service: 'VA disability appeals, discharge upgrades, legal representation',
    organization: 'Central Texas Legal Aid',
    status: 'Active',
    geographicArea: null,
    contactInfo: null,
    eligibilityNotes: 'Income-eligible veterans and surviving spouses.',
    createdAt: '2026-01-01T00:00:00Z',
    updatedAt: '2026-05-29T08:00:00Z',
  },
  {
    id: 'res-3',
    name: 'Bastrop County Veterans Transportation Program',
    category: 'Transportation',
    county: 'Bastrop',
    service: 'Medical appointment transport and transit vouchers for veterans',
    organization: 'Bastrop County VSO',
    status: 'Active',
    geographicArea: null,
    contactInfo: null,
    eligibilityNotes: 'Bastrop County veterans with no vehicle or medical need.',
    createdAt: '2026-01-01T00:00:00Z',
    updatedAt: '2026-05-28T10:00:00Z',
  },
  {
    id: 'res-4',
    name: 'Central Texas Food Bank – Veterans Program',
    category: 'Food & Nutrition',
    county: 'Williamson',
    service: 'Emergency food boxes and SNAP application assistance',
    organization: 'Central Texas Food Bank',
    status: 'Active',
    geographicArea: null,
    contactInfo: null,
    eligibilityNotes: null,
    createdAt: '2026-01-01T00:00:00Z',
    updatedAt: '2026-05-27T10:00:00Z',
  },
  {
    id: 'res-5',
    name: 'VA Austin Mental Health Clinic',
    category: 'Mental Health Services',
    county: 'Travis',
    service: 'Individual counseling, PTSD treatment, crisis intervention',
    organization: 'VA Austin Healthcare System',
    status: 'Active',
    geographicArea: null,
    contactInfo: null,
    eligibilityNotes: 'Must be enrolled in VA healthcare.',
    createdAt: '2026-01-01T00:00:00Z',
    updatedAt: '2026-05-26T10:00:00Z',
  },
  {
    id: 'res-6',
    name: 'Texas Workforce Solutions – Veterans Employment Services',
    category: 'Employment & Vocational Rehab',
    county: 'Statewide',
    service: 'Job placement, resume assistance, and vocational rehabilitation',
    organization: 'Texas Workforce Commission',
    status: 'Active',
    geographicArea: 'Statewide',
    contactInfo: null,
    eligibilityNotes: 'Priority of service for veterans and eligible spouses.',
    createdAt: '2026-01-01T00:00:00Z',
    updatedAt: '2026-05-25T10:00:00Z',
  },
  {
    id: 'res-other',
    name: 'Other (specify in notes)',
    category: 'Other',
    county: 'Statewide',
    service: 'Other service not listed',
    organization: '—',
    status: 'Active',
    geographicArea: 'Statewide',
    contactInfo: null,
    eligibilityNotes: 'Use this option when the service or resource is not listed above. Describe the referral in the Notes field.',
    createdAt: '2026-01-01T00:00:00Z',
    updatedAt: '2026-01-01T00:00:00Z',
  },
];

// ---------------------------------------------------------------------------
// Documents — from demo.html line 403-407
// ---------------------------------------------------------------------------

export const seedDocuments: VcmDocument[] = [
  {
    id: 'doc-1',
    caseId: 'CASE-4879-10482',
    clientId: 'VCM-10482',
    fileName: 'release_of_information_signed.pdf',
    documentType: 'Release of Information',
    notes: null,
    fileSize: '484 KB',
    uploadDate: 'May 16, 2026',
    uploadedBy: 'Noah Golding',
  },
  {
    id: 'doc-2',
    caseId: 'CASE-4879-10482',
    clientId: 'VCM-10482',
    fileName: 'benefits_award_letter_may.pdf',
    documentType: 'Benefits Award Letter',
    notes: null,
    fileSize: '1.2 MB',
    uploadDate: 'May 22, 2026',
    uploadedBy: 'Noah Golding',
  },
  {
    id: 'doc-3',
    caseId: 'CASE-4879-10482',
    clientId: 'VCM-10482',
    fileName: 'dd214_martinez.pdf',
    documentType: 'DD-214 (Discharge Papers)',
    notes: null,
    fileSize: '2.1 MB',
    uploadDate: 'May 22, 2026',
    uploadedBy: 'Noah Golding',
  },
  {
    id: 'doc-4',
    caseId: 'CASE-4879-10541',
    clientId: 'VCM-10541',
    fileName: 'release_of_info_chen.pdf',
    documentType: 'Release of Information',
    notes: null,
    fileSize: '320 KB',
    uploadDate: 'May 22, 2026',
    uploadedBy: 'Noah Golding',
  },
  {
    id: 'doc-5',
    caseId: 'CASE-4879-10541',
    clientId: 'VCM-10541',
    fileName: 'disability_rating_chen.pdf',
    documentType: 'Disability Rating Documentation',
    notes: 'Service-connected disability rating from VA',
    fileSize: '156 KB',
    uploadDate: 'May 24, 2026',
    uploadedBy: 'Noah Golding',
  },
  {
    id: 'doc-6',
    caseId: 'CASE-4879-10541',
    clientId: 'VCM-10541',
    fileName: 'housing_application_chen.pdf',
    documentType: 'Housing Application',
    notes: null,
    fileSize: '890 KB',
    uploadDate: 'May 24, 2026',
    uploadedBy: 'Noah Golding',
  },
  {
    id: 'doc-7',
    caseId: 'CASE-4879-10541',
    clientId: 'VCM-10541',
    fileName: 'va_claim_determination_chen.pdf',
    documentType: 'Benefits Award Letter',
    notes: null,
    fileSize: '290 KB',
    uploadDate: 'May 28, 2026',
    uploadedBy: 'Noah Golding',
  },
  {
    id: 'doc-8',
    caseId: 'CASE-4879-10555',
    clientId: 'VCM-10555',
    fileName: 'release_of_information_williams.pdf',
    documentType: 'Release of Information',
    notes: null,
    fileSize: '412 KB',
    uploadDate: 'May 25, 2026',
    uploadedBy: 'Marcus Lee',
  },
  {
    id: 'doc-9',
    caseId: 'CASE-4879-10555',
    clientId: 'VCM-10555',
    fileName: 'mental_health_records_summary.pdf',
    documentType: 'Mental Health Treatment Records',
    notes: 'Mobility and mental health assessment from VA primary care',
    fileSize: '1.8 MB',
    uploadDate: 'May 28, 2026',
    uploadedBy: 'Marcus Lee',
  },
  {
    id: 'doc-10',
    caseId: 'CASE-4879-10562',
    clientId: 'VCM-10562',
    fileName: 'va_denial_letter_rivera.pdf',
    documentType: 'Legal Correspondence',
    notes: 'VA disability claim denial — appeal in planning',
    fileSize: '198 KB',
    uploadDate: 'May 25, 2026',
    uploadedBy: 'Noah Golding',
  },
  {
    id: 'doc-11',
    caseId: 'CASE-4879-10562',
    clientId: 'VCM-10562',
    fileName: 'va_healthcare_enrollment_rivera.pdf',
    documentType: 'VA Healthcare Enrollment',
    notes: null,
    fileSize: '445 KB',
    uploadDate: 'May 26, 2026',
    uploadedBy: 'Noah Golding',
  },
];

// ---------------------------------------------------------------------------
// Address Records — from demo.html line 385-391 (for address search mode)
// ---------------------------------------------------------------------------

export const seedAddressRecords: AddressRecord[] = [
  {
    clientId: 'VCM-10482',
    clientName: 'Elena Martinez',
    addressType: 'Primary',
    address: '407 Willow Bend Dr, Georgetown, TX 78626',
    county: 'Williamson',
    program: 'SSVF – Austin Area',
    programCode: 'SSVF-TX-2026-001',
  },
  {
    clientId: 'VCM-10482',
    clientName: 'Elena Martinez',
    addressType: 'Mailing',
    address: '1801 Meadow View Rd, Georgetown, TX 78628',
    county: 'Williamson',
    program: 'SSVF – Austin Area',
    programCode: 'SSVF-TX-2026-001',
  },
  {
    clientId: 'VCM-10496',
    clientName: 'James Nguyen',
    addressType: 'Primary',
    address: '901 Pecan Grove Rd, Taylor, TX 76574',
    county: 'Williamson',
    program: 'SSVF – Austin Area',
    programCode: 'SSVF-TX-2026-001',
  },
  {
    clientId: 'VCM-10517',
    clientName: 'Aisha Thompson',
    addressType: 'Primary',
    address: '61 Garden St, Austin, TX 78702',
    county: 'Travis',
    program: 'SSVF – Austin Area',
    programCode: 'SSVF-TX-2026-001',
  },
  {
    clientId: 'VCM-10528',
    clientName: 'Robert Jackson',
    addressType: 'Primary',
    address: '18 County Road 419, Bastrop, TX 78602',
    county: 'Bastrop',
    program: 'SSVF – Austin Area',
    programCode: 'SSVF-TX-2026-001',
  },
  {
    clientId: 'VCM-09832',
    clientName: 'Maria Hernandez',
    addressType: 'Primary',
    address: '200 Oak Meadow Ln, Seguin, TX 78155',
    county: 'Guadalupe',
    program: 'Veterans Housing Assistance – South TX',
    programCode: 'VHAP-TX-2026-001',
  },
  {
    clientId: 'VCM-10541',
    clientName: 'David Chen',
    addressType: 'Primary',
    address: '145 Brushy Creek Rd, Round Rock, TX 78664',
    county: 'Williamson',
    program: 'SSVF – Austin Area',
    programCode: 'SSVF-TX-2026-001',
  },
  {
    clientId: 'VCM-10555',
    clientName: 'Patricia Williams',
    addressType: 'Primary',
    address: '820 Congress Ave Unit 4B, Austin, TX 78701',
    county: 'Travis',
    program: 'SSVF – Austin Area',
    programCode: 'SSVF-TX-2026-001',
  },
  {
    clientId: 'VCM-10562',
    clientName: 'Samuel Rivera',
    addressType: 'Primary',
    address: '302 Pine St, Bastrop, TX 78602',
    county: 'Bastrop',
    program: 'SSVF – Austin Area',
    programCode: 'SSVF-TX-2026-001',
  },
  {
    clientId: 'VCM-10562',
    clientName: 'Samuel Rivera',
    addressType: 'Mailing',
    address: '1500 Chestnut St, Bastrop, TX 78602',
    county: 'Bastrop',
    program: 'SSVF – Austin Area',
    programCode: 'SSVF-TX-2026-001',
  },
];

// ---------------------------------------------------------------------------
// Call Log — phone triage records
// ---------------------------------------------------------------------------

export const seedCallLog: CallLogEntry[] = [
  {
    id: 'call-1', clientId: 'CALL-001', caseId: null, caseName: null, callerName: 'Rosa Delgado', callerPhone: '512-555-0777',
    callerEmail: 'rosa.delgado@email.com', reasonForCall: 'Information request',
    notes: 'Caller asked about available veterans housing assistance programs. Provided overview of SSVF services and eligibility requirements.',
    referralId: null, referralName: null, handledBy: 'Noah Golding', outcome: 'Contact Only',
    followUpNeeded: false,
    createdAt: '2026-08-01T10:15:00Z',
  },
  {
    id: 'call-2', clientId: 'CALL-001', caseId: null, caseName: null, callerName: 'Rosa Delgado', callerPhone: '512-555-0777',
    callerEmail: 'rosa.delgado@email.com', reasonForCall: 'Referral needed',
    notes: 'Follow-up call. Caller now interested in emergency food assistance for veterans. Referred to Central Texas Food Bank Veterans Program.',
    referralId: 'res-4', referralName: 'Central Texas Food Bank – Veterans Program', handledBy: 'Marcus Lee', outcome: 'Referred',
    followUpNeeded: true,
    createdAt: '2026-08-08T14:00:00Z',
  },
  {
    id: 'call-3', clientId: 'CALL-002', caseId: null, caseName: null, callerName: 'Frank Morrison', callerPhone: '737-555-0333',
    callerEmail: null, reasonForCall: 'Information request',
    notes: 'Veteran (63) asking about VA disability claims process and available mental health services. Provided information on TVC benefits counseling.',
    referralId: 'res-1', referralName: 'Texas Veterans Commission – Benefits Navigation', handledBy: 'Noah Golding', outcome: 'Referred',
    followUpNeeded: false,
    createdAt: '2026-08-03T11:30:00Z',
  },
  {
    id: 'call-4', clientId: 'VCM-10482', caseId: 'CASE-4879-10482', caseName: 'CASE-4879-10482 — SSVF Housing Assistance', callerName: 'Elena Martinez', callerPhone: '512-555-0134',
    callerEmail: 'elena.martinez@example.org', reasonForCall: 'Follow-up on previous call',
    notes: 'Registered veteran called to check on VA disability claim status. Reviewed timeline and next steps for benefits counseling appointment.',
    referralId: null, referralName: null, handledBy: 'Noah Golding', outcome: 'Registered',
    followUpNeeded: true,
    createdAt: '2026-08-09T09:00:00Z',
  },
];
